import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import L from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet-draw';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import { formatDate, stripHtml, uid, daysBetween } from '../utils';
import { nominatimSearch } from '../nominatim';
import Modal from './Modal';

const toYtEmbedUrl = (url) => {
  if (!url) return null;
  try {
    const u    = new URL(url.trim());
    const host = u.hostname.replace('www.', '').replace('m.', '');
    let id = null;
    if (host === 'youtu.be') { id = u.pathname.slice(1).split('/')[0]; }
    else if (host === 'youtube.com' || host === 'youtube-nocookie.com') {
      if (u.pathname.startsWith('/embed/') || u.pathname.startsWith('/shorts/')) { id = u.pathname.split('/')[2]; }
      else { id = u.searchParams.get('v'); }
    }
    if (id && /^[a-zA-Z0-9_-]{11}$/.test(id)) return `https://www.youtube-nocookie.com/embed/${id}`;
  } catch {}
  return null;
};

// ─── MAP TIMELINE ──────────────────────────────────────────────────────────────
//
//  Tier 1: Nominatim compliance, offline fallback, coord validation, --vh, toast
//  Tier 2: Route lines, category pins, heatmap, map search, event filter
//  Tier 3: Satellite tiles, measurement tool, weather (Open-Meteo), PNG/PDF export
//
//  Props: events, colors, onEventClick, manualOrder, mapCenter
//

const SPEEDS = [
  { label: 'Slow',   ms: 6000 },
  { label: 'Normal', ms: 3000 },
  { label: 'Fast',   ms: 1500 },
];

const FALLBACK_TILE_URL =
  import.meta.env?.VITE_FALLBACK_TILE_URL ||
  'https://tile.openstreetmap.de/{z}/{x}/{y}.png';

// Stadia Maps (https://docs.stadiamaps.com) gates Stamen-style tiles behind
// rate limits without an API key — for any production-style traffic we need
// to append ?api_key=… to every request.  Set VITE_STADIA_MAPS_API_KEY in
// the .env to enable Watercolor + Antique reliably; tiles will still work
// at low volume without it, but the user will hit limits quickly.
const STADIA_KEY  = import.meta.env?.VITE_STADIA_MAPS_API_KEY || '';
const STADIA_AUTH = STADIA_KEY ? `?api_key=${STADIA_KEY}` : '';

const TILES = {
  standard: {
    label: 'Standard',
    url:   'https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png',
    attr:  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    sub:   'abc',
  },
  minimal: {
    label: 'Minimal',
    url:   'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
    attr:  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    sub:   'abcd',
  },
  dark: {
    label: 'Dark',
    url:   'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attr:  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy; <a href="https://carto.com/attributions">CARTO</a>',
    sub:   'abcd',
  },
  terrain: {
    // OpenTopoMap is hosted by volunteers and can be slow; rely on Leaflet's
    // built-in retry on tile errors instead of falling back aggressively.
    label: 'Terrain',
    url:   'https://{s}.tile.opentopomap.org/{z}/{x}/{y}.png',
    attr:  'Map data: &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>, <a href="http://viewfinderpanoramas.org">SRTM</a> | &copy; <a href="https://opentopomap.org">OpenTopoMap</a>',
    sub:   'abc',
    maxZoom: 17, // OpenTopoMap caps at 17
  },
  watercolor: {
    label: 'Watercolor',
    url:   `https://tiles.stadiamaps.com/tiles/stamen_watercolor/{z}/{x}/{y}.jpg${STADIA_AUTH}`,
    attr:  '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://stamen.com/">Stamen Design</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    sub:   '',
    maxZoom: 16, // Stadia stamen_watercolor cap
  },
  antique: {
    label: 'Antique',
    url:   `https://tiles.stadiamaps.com/tiles/stamen_toner/{z}/{x}/{y}{r}.png${STADIA_AUTH}`,
    attr:  '&copy; <a href="https://stadiamaps.com/">Stadia Maps</a> &copy; <a href="https://stamen.com/">Stamen Design</a> &copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
    sub:   '',
    maxZoom: 20,
  },
  // Kept for backward compatibility with timelines that previously selected
  // satellite; not surfaced in the new style picker.
  satellite: {
    label: 'Satellite',
    url:   'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attr:  'Tiles &copy; Esri',
    sub:   '',
  },
  fallback: {
    label: 'Fallback',
    url:   FALLBACK_TILE_URL,
    attr:  '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    sub:   '',
  },
};
// Ordered list shown in the Design-tab picker and the on-map tile toggle.
const TILE_STYLES = ['standard', 'minimal', 'dark', 'terrain', 'watercolor', 'antique'];

// ─── WMO weather code lookup ───────────────────────────────────────────────────

const WMO = {
  0:  ['Clear sky',          '☀️'],  1:  ['Mainly clear',       '🌤️'],
  2:  ['Partly cloudy',      '⛅'],  3:  ['Overcast',           '☁️'],
  45: ['Fog',                '🌫️'],  48: ['Rime fog',           '🌫️'],
  51: ['Light drizzle',      '🌦️'],  53: ['Drizzle',            '🌦️'],  55: ['Dense drizzle',      '🌦️'],
  61: ['Light rain',         '🌧️'],  63: ['Rain',               '🌧️'],  65: ['Heavy rain',         '🌧️'],
  71: ['Light snow',         '❄️'],  73: ['Snow',               '❄️'],  75: ['Heavy snow',         '❄️'],
  77: ['Snow grains',        '🌨️'],
  80: ['Rain showers',       '🌦️'],  81: ['Rain showers',       '🌦️'],  82: ['Violent showers',    '⛈️'],
  85: ['Snow showers',       '🌨️'],  86: ['Heavy snow showers', '🌨️'],
  95: ['Thunderstorm',       '⛈️'],  96: ['Thunderstorm',       '⛈️'],  99: ['Thunderstorm',       '⛈️'],
};

// ─── Category color palette ────────────────────────────────────────────────────

const CAT_PALETTE = [
  '#ef4444','#f97316','#eab308','#22c55e','#06b6d4','#3b82f6',
  '#8b5cf6','#ec4899','#84cc16','#14b8a6','#a855f7','#78716c',
];
const _catMap = {};
let _catIdx = 0;

function categoryColor(ev, fallback) {
  if (!ev?.category) return fallback;
  const key = ev.category.toLowerCase();
  if (!_catMap[key]) _catMap[key] = CAT_PALETTE[_catIdx++ % CAT_PALETTE.length];
  return _catMap[key];
}

// ─── Coordinate validation ─────────────────────────────────────────────────────

function isValidLat(v) { return typeof v === 'number' && isFinite(v) && v >= -90  && v <= 90;  }
function isValidLng(v) { return typeof v === 'number' && isFinite(v) && v >= -180 && v <= 180; }
function hasValidCoords(ev) {
  return isValidLat(ev?.location?.lat) && isValidLng(ev?.location?.lng);
}

// ─── Misc helpers ──────────────────────────────────────────────────────────────

function detectDarkTheme() {
  const t = document.documentElement.dataset.theme;
  if (t) return t === 'dark';
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false;
}

function kmBetween(lat1, lng1, lat2, lng2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * (Math.PI / 180)) * Math.cos(lat2 * (Math.PI / 180)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function formatKm(km) {
  return km >= 1000
    ? `${(km / 1000).toFixed(1)} Mm`
    : km >= 1
    ? `${Math.round(km)} km`
    : `${Math.round(km * 1000)} m`;
}

function routeDistance(events) {
  const pts = events.filter(hasValidCoords);
  let total = 0;
  for (let i = 1; i < pts.length; i++) {
    total += kmBetween(
      pts[i - 1].location.lat, pts[i - 1].location.lng,
      pts[i].location.lat,     pts[i].location.lng
    );
  }
  return total;
}

function makePinIcon(isActive, primary, secondary, pinColor = null) {
  const color = isActive ? secondary : (pinColor || primary);
  const size  = isActive ? 22 : 14;
  const pulse = isActive
    ? `<div class="mp-pin-pulse" style="border-color:${secondary};"></div>`
    : '';
  return L.divIcon({
    className: 'mp-pin-host',
    html: `<div class="mp-pin${isActive ? ' mp-pin-active' : ''}" style="width:${size}px;height:${size}px;background:${color};box-shadow:0 0 0 2px #fff, 0 2px 8px rgba(0,0,0,.4);">${pulse}</div>`,
    iconSize:    [size + 8, size + 8],
    iconAnchor:  [(size + 8) / 2, size + 8],
    popupAnchor: [0, -(size + 8)],
  });
}

// ─── Per-event map icon badges ────────────────────────────────────────────────
//
//  Twelve picker options.  Each is rendered as a 32px circular badge in the
//  event's color (or Chronos gold fallback) with a centered emoji.  Active
//  events bump up slightly; the CSS handles hover scaling to 40px.
//
export const MAP_ICONS = {
  pin:    { emoji: '📍', label: 'Default Pin' },
  sword:  { emoji: '⚔️', label: 'Sword' },
  crown:  { emoji: '👑', label: 'Crown' },
  column: { emoji: '🏛️', label: 'Column' },
  skull:  { emoji: '💀', label: 'Skull' },
  flame:  { emoji: '🔥', label: 'Flame' },
  ship:   { emoji: '🚢', label: 'Ship' },
  book:   { emoji: '📖', label: 'Book' },
  coin:   { emoji: '🪙', label: 'Coin' },
  star:   { emoji: '⭐', label: 'Star' },
  flag:   { emoji: '🚩', label: 'Flag' },
  person: { emoji: '👤', label: 'Person' },
};

// Three preset badge sizes — emoji scales proportionally.  Active state bumps
// the badge up by 4px (matching the existing pin behavior).
const BADGE_SIZES = { small: 24, medium: 32, large: 44 };
function makeIconBadge(iconKey, color, isActive, sizeKey = 'medium') {
  const def  = MAP_ICONS[iconKey] || MAP_ICONS.pin;
  const base = BADGE_SIZES[sizeKey] ?? BADGE_SIZES.medium;
  const size = isActive ? base + 4 : base;
  // Keep emoji ~62% of the badge dimension
  const emojiSize = Math.round(size * 0.55);
  return L.divIcon({
    className: 'mp-icon-badge-host',
    html: `<div class="mp-icon-badge${isActive ? ' mp-icon-badge-active' : ''}" style="width:${size}px;height:${size}px;background:${color};"><span class="mp-icon-badge-emoji" style="font-size:${emojiSize}px;">${def.emoji}</span></div>`,
    iconSize:    [size, size],
    iconAnchor:  [size / 2, size / 2],
    popupAnchor: [0, -size / 2],
  });
}

// ─── Region fill patterns (SVG defs) ─────────────────────────────────────────
//
//  Leaflet draws polygons in an SVG element on the overlay pane.  To use
//  pattern fills we inject a single hidden <defs> block at body level (so any
//  Leaflet svg in the document can reference it via fill="url(#...)") and
//  lazily add patterns the first time a (style, color) pair is requested.
//
function ensurePatternDefs() {
  let host = document.getElementById('mp-pattern-svg');
  if (host) return host.querySelector('defs');
  const svgNS = 'http://www.w3.org/2000/svg';
  host = document.createElementNS(svgNS, 'svg');
  host.setAttribute('id', 'mp-pattern-svg');
  host.setAttribute('width', '0');
  host.setAttribute('height', '0');
  host.setAttribute('aria-hidden', 'true');
  host.style.position  = 'absolute';
  host.style.overflow  = 'hidden';
  const defs = document.createElementNS(svgNS, 'defs');
  host.appendChild(defs);
  document.body.appendChild(host);
  return defs;
}

function ensurePattern(style, color) {
  const defs = ensurePatternDefs();
  const safeColor = String(color || '#C9A96E').replace(/[^0-9a-fA-F]/g, '');
  const id = `mp-pat-${style}-${safeColor}`;
  if (document.getElementById(id)) return id;
  const ns = 'http://www.w3.org/2000/svg';
  const pat = document.createElementNS(ns, 'pattern');
  pat.setAttribute('id', id);
  pat.setAttribute('patternUnits', 'userSpaceOnUse');
  pat.setAttribute('width',  '10');
  pat.setAttribute('height', '10');
  if (style === 'hatching') {
    const line = document.createElementNS(ns, 'line');
    line.setAttribute('x1', '0');  line.setAttribute('y1', '0');
    line.setAttribute('x2', '10'); line.setAttribute('y2', '10');
    line.setAttribute('stroke', color);
    line.setAttribute('stroke-width', '1.5');
    line.setAttribute('stroke-linecap', 'square');
    pat.appendChild(line);
  } else if (style === 'crosshatch') {
    const l1 = document.createElementNS(ns, 'line');
    l1.setAttribute('x1', '0');  l1.setAttribute('y1', '0');
    l1.setAttribute('x2', '10'); l1.setAttribute('y2', '10');
    l1.setAttribute('stroke', color);
    l1.setAttribute('stroke-width', '1.5');
    pat.appendChild(l1);
    const l2 = document.createElementNS(ns, 'line');
    l2.setAttribute('x1', '0');  l2.setAttribute('y1', '10');
    l2.setAttribute('x2', '10'); l2.setAttribute('y2', '0');
    l2.setAttribute('stroke', color);
    l2.setAttribute('stroke-width', '1.5');
    pat.appendChild(l2);
  } else if (style === 'dots') {
    const c = document.createElementNS(ns, 'circle');
    c.setAttribute('cx', '5'); c.setAttribute('cy', '5'); c.setAttribute('r', '1.5');
    c.setAttribute('fill', color);
    pat.appendChild(c);
  }
  defs.appendChild(pat);
  return id;
}

// Normalize a region's linked-event field.  Older saves stored a single
// `linkedEventId` string; the current schema is `linkedEventIds` (array of
// ids).  This returns the array form regardless of which shape was saved.
function getRegionLinkedIds(region) {
  if (!region) return [];
  if (Array.isArray(region.linkedEventIds)) return region.linkedEventIds.filter(Boolean);
  if (region.linkedEventId) return [region.linkedEventId];
  return [];
}

// Maps borderStyle string to a Leaflet dashArray value (null = solid line).
function borderDashArray(style, thickness) {
  if (style === 'dashed') return `${Math.max(6, thickness * 3)},${Math.max(3, thickness * 2)}`;
  if (style === 'dotted') return `${Math.max(1, thickness)},${Math.max(3, thickness * 2)}`;
  return null;
}

function makeClusterIcon(count, primary) {
  return L.divIcon({
    className: 'mp-cluster-host',
    html: `<div class="mp-cluster" style="background:${primary};">${count}</div>`,
    iconSize:   [36, 36],
    iconAnchor: [18, 18],
  });
}

// Uses html2canvas with useCORS so cross-origin OSM/ESRI tiles render correctly.
// UI overlays are excluded via ignoreElements so only the map canvas is captured.
async function getMapCanvas(mapEl) {
  return html2canvas(mapEl, {
    useCORS:      true,
    allowTaint:   false,
    logging:      false,
    scale:        window.devicePixelRatio || 1,
    // Ignore UI controls overlaid on the map — only capture the tile/marker layer
    ignoreElements: (el) => el.classList?.contains('mp-map-controls') ||
                            el.classList?.contains('mp-tile-toggle')   ||
                            el.classList?.contains('mp-map-search')    ||
                            el.classList?.contains('mp-region-toolbar')||
                            el.classList?.contains('mp-region-popover')||
                            el.classList?.contains('mp-preview')       ||
                            el.classList?.contains('mp-preview-reopen')||
                            el.classList?.contains('mp-offline-banner')||
                            el.classList?.contains('mp-measure-banner')||
                            el.classList?.contains('mp-toast'),
  });
}

// ─── Linked-event popup card ─────────────────────────────────────────────────
//
//  Rendered when the viewer clicks a region with one or more linked events.
//  Desktop: fixed card top-centered over the map.  Mobile: bottom sheet
//  sliding up.  When the region is linked to multiple events the body becomes
//  a scrollable list of event entries.
//
const LinkedEventPopup = ({ events, onClose, onView, primary }) => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;
  useEffect(() => {
    const onDocClick = (e) => {
      if (e.target.closest?.('.mp-linked-popup')) return;
      onClose();
    };
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    const t = setTimeout(() => {
      document.addEventListener('click', onDocClick);
      document.addEventListener('keydown', onKey);
    }, 0);
    return () => {
      clearTimeout(t);
      document.removeEventListener('click', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);
  const list = Array.isArray(events) ? events : (events ? [events] : []);
  if (list.length === 0) return null;
  const multi = list.length > 1;
  return (
    <div className={`mp-linked-popup${isMobile ? ' mp-linked-popup-sheet' : ''}${multi ? ' mp-linked-popup-multi' : ''}`} role="dialog" aria-label="Linked event preview">
      <button type="button" className="mp-linked-popup-close" onClick={onClose} aria-label="Close">✕</button>
      <div className={multi ? 'mp-linked-popup-scroll' : ''}>
        {list.map((event, i) => {
          const desc      = event.description ? stripHtml(event.description) : '';
          const truncated = desc.length > 150 ? desc.slice(0, 150) : desc;
          const dateStr   = formatDate(event.startDate);
          return (
            <div key={event.id || i} className="mp-linked-popup-entry">
              {event.photo && (
                <img src={event.photo} alt="" className="mp-linked-popup-photo" loading="lazy" />
              )}
              <div className="mp-linked-popup-body">
                <div className="mp-linked-popup-date">{dateStr}</div>
                <div className="mp-linked-popup-title">{event.title}</div>
                {truncated && (
                  <p className="mp-linked-popup-desc">
                    {truncated}{desc.length > 150 ? '…' : ''}
                    {desc.length > 150 && (
                      <button type="button" className="mp-linked-popup-more" onClick={() => onView(event)}>Read more</button>
                    )}
                  </p>
                )}
                <button type="button" className="mp-linked-popup-view" onClick={() => onView(event)} style={{ background: primary }}>
                  View Details
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const MapTimeline = ({ events = [], colors = {}, onEventClick, manualOrder = false, mapCenter = null, timeline = null, isEmbed = false, canEdit = false, onColorsChange }) => {
  const primary   = colors.primary   || '#FFB612';
  const secondary = colors.secondary || '#A8A9AD';

  const sorted = useMemo(() => {
    return [...events].sort((a, b) => {
      if (manualOrder) return (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
      const d = (a.startDate ?? '').localeCompare(b.startDate ?? '');
      return d !== 0 ? d : (a.sortOrder ?? 0) - (b.sortOrder ?? 0);
    });
  }, [events, manualOrder]);

  // Hoisted up here so the date-scrubber effects (and region-render effect)
  // can list `regions` in their dep arrays without hitting a TDZ error in
  // production builds (where the const is renamed to a 2-char identifier).
  const regions = useMemo(
    () => Array.isArray(colors?.mapRegions) ? colors.mapRegions : [],
    [colors?.mapRegions]
  );

  // Standalone map labels (Feature 1). Same hoisting reason as `regions`.
  const labels = useMemo(
    () => Array.isArray(colors?.mapLabels) ? colors.mapLabels : [],
    [colors?.mapLabels]
  );

  // ── Core state ─────────────────────────────────────────────────────────────
  const [activeIdx,     setActiveIdx]     = useState(0);
  // Initialize the tile style from colors.mapStyle (preferred) and fall back
  // to a theme-matched default so existing timelines keep working.
  const [tileStyle, setTileStyle] = useState(() => {
    const saved = colors?.mapStyle;
    if (saved && TILES[saved]) return saved;
    return detectDarkTheme() ? 'dark' : 'standard';
  });
  const [playing,       setPlaying]       = useState(false);
  const [progress,      setProgress]      = useState(0);
  const [playSpeed,     setPlaySpeed]     = useState(1);
  const [showPreview,   setShowPreview]   = useState(false);
  const [mapReady,      setMapReady]      = useState(false);
  const [showControls,  setShowControls]  = useState(() => window.innerWidth > 480);
  // Collapse the legend by default on small viewports so it doesn't eat into
  // limited map space — the user can still expand with one tap.
  const [legendCollapsed, setLegendCollapsed] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth <= 480
  );

  // ── Tier 1 state ───────────────────────────────────────────────────────────
  const [offlineBanner, setOfflineBanner] = useState(false);
  const [toast,         setToast]         = useState('');
  const [toastVisible,  setToastVisible]  = useState(false);

  // ── Tier 2 state ───────────────────────────────────────────────────────────
  const [showRoute,     setShowRoute]     = useState(false);
  const [showHeatmap,   setShowHeatmap]   = useState(false);
  const [mapSearch,     setMapSearch]     = useState('');
  const [mapSearchBusy, setMapSearchBusy] = useState(false);
  const [filterQuery,   setFilterQuery]   = useState('');

  // ── Tier 3 state ───────────────────────────────────────────────────────────
  const [measureMode,   setMeasureMode]   = useState(false);
  const [measurePts,    setMeasurePts]    = useState([]);
  const [weather,       setWeather]       = useState(null);
  const [weatherUnit,   setWeatherUnit]   = useState('C');
  const [exporting,     setExporting]     = useState(false);
  const [embedOpen,     setEmbedOpen]     = useState(false);
  const [embedCopied,   setEmbedCopied]   = useState(false);

  // Compute the timeline's [min, max] date range for the scrubber and for
  // mapping each event/region into a 0..1 visibility fraction.
  const { scrubMin, scrubMax, scrubSpan } = useMemo(() => {
    const dates = [];
    sorted.forEach(ev => {
      if (ev?.startDate) dates.push(ev.startDate);
      if (ev?.endDate)   dates.push(ev.endDate);
    });
    if (dates.length === 0) return { scrubMin: '', scrubMax: '', scrubSpan: 1 };
    dates.sort();
    const min = dates[0];
    const max = dates[dates.length - 1];
    const span = Math.max(1, daysBetween(min, max) || 1);
    return { scrubMin: min, scrubMax: max, scrubSpan: span };
  }, [sorted]);

  // Helper: convert a date string into the 0..1 fraction over [scrubMin, scrubMax].
  const dateToFrac = useCallback((d) => {
    if (!d || !scrubMin || !scrubMax) return 0;
    const f = daysBetween(scrubMin, d) / scrubSpan;
    return Math.max(0, Math.min(1, f));
  }, [scrubMin, scrubMax, scrubSpan]);

  // ── Date scrubber (Feature 4) ──────────────────────────────────────────────
  // A horizontal slider over the timeline's [minDate, maxDate] range that
  // dims out-of-range markers (30% opacity) and shows/hides regions according
  // to each region's optional visibleFrom / visibleUntil window.
  //
  // The scrub value is encoded as a 0..1 fraction (numeric) so the <input
  // type="range"> works regardless of whether the timeline's dates are
  // BC-era keys or plain YYYY-MM-DD strings — we map between fraction and
  // the underlying string using the same `daysBetween` helper events use.
  const [scrubFrac,    setScrubFrac]    = useState(1.0); // 1.0 = "end" (everything visible by default)
  const [scrubPlaying, setScrubPlaying] = useState(false);
  const [scrubSpeed,   setScrubSpeed]   = useState(1);   // 0/1/2 = slow/normal/fast
  const SCRUB_SPEEDS = useMemo(() => [
    { label: 'Slow',   ms: 12000 },
    { label: 'Normal', ms:  6000 },
    { label: 'Fast',   ms:  2500 },
  ], []);

  // Human-readable label for the current scrub position (interpolated year)
  const scrubLabel = useMemo(() => {
    if (!scrubMin || !scrubMax || scrubMin.length < 12) return '';
    const minBiased = parseInt(scrubMin.slice(0, 6), 10);
    const maxBiased = parseInt(scrubMax.slice(0, 6), 10);
    if (!isFinite(minBiased) || !isFinite(maxBiased)) return '';
    const cur = Math.round(minBiased + scrubFrac * (maxBiased - minBiased));
    const astro = cur - 99999;
    if (astro >= 1) return String(astro);
    if (astro === 0) return '1 BC';
    return `${1 - astro} BC`;
  }, [scrubMin, scrubMax, scrubFrac]);

  // Marker dimming: events past the current scrub date fade to 30% opacity.
  useEffect(() => {
    if (!mapReady) return;
    const markers = markersRef.current;
    if (!markers) return;
    sorted.forEach(ev => {
      const m = markers[ev.id];
      if (!m || typeof m.setOpacity !== 'function') return;
      if (scrubFrac >= 1 || !ev.startDate) { m.setOpacity(1); return; }
      const evFrac = dateToFrac(ev.startDate);
      m.setOpacity(evFrac <= scrubFrac ? 1 : 0.3);
    });
  }, [scrubFrac, sorted, dateToFrac, mapReady]);

  // Region visibility: respect visibleFrom / visibleUntil against scrub date.
  useEffect(() => {
    Object.entries(regionLayersRef.current).forEach(([id, entry]) => {
      if (!entry) return;
      const region = regions.find(r => r.id === id);
      if (!region) return;
      let visible = true;
      if (region.visibleFrom && dateToFrac(region.visibleFrom) > scrubFrac) visible = false;
      if (region.visibleUntil && dateToFrac(region.visibleUntil) < scrubFrac) visible = false;
      const pathEl  = entry.poly?.getElement?.();
      const labelEl = entry.label?.getElement?.();
      if (pathEl)  pathEl.style.opacity  = visible ? '' : '0';
      if (labelEl) labelEl.style.opacity = visible ? '' : '0';
    });
  }, [scrubFrac, regions, dateToFrac]);

  // Auto-play loop for the date scrubber.
  useEffect(() => {
    if (!scrubPlaying) return;
    const totalMs = SCRUB_SPEEDS[scrubSpeed]?.ms || 6000;
    const tickMs  = 50;
    const step    = tickMs / totalMs;
    const id = setInterval(() => {
      setScrubFrac(prev => {
        const next = prev + step;
        if (next >= 1) { setScrubPlaying(false); return 1; }
        return next;
      });
    }, tickMs);
    return () => clearInterval(id);
  }, [scrubPlaying, scrubSpeed, SCRUB_SPEEDS]);

  const toggleScrubPlay = useCallback(() => {
    if (scrubFrac >= 1) setScrubFrac(0);
    setScrubPlaying(p => !p);
  }, [scrubFrac]);

  // ── Region drawing ─────────────────────────────────────────────────────────
  // mapRegions is the persisted array; pending = polygon just drawn awaiting
  // popover form completion; editingRegion = an existing region being edited.
  // (`regions` itself was hoisted above the scrubber/date effects since those
  // effects reference it in their dep arrays.)
  const [drawMode,      setDrawMode]      = useState(false);
  const [deleteMode,    setDeleteMode]    = useState(false);
  const [pendingRegion, setPendingRegion] = useState(null); // { coordinates } before save
  const [editingRegion, setEditingRegion] = useState(null); // { id, label, color, opacity, coordinates }
  // Linked-event interaction state (ENH 2)
  const [highlightedRegionIds, setHighlightedRegionIds] = useState([]);
  const [linkedEventPopup,    setLinkedEventPopup]    = useState(null); // { event, anchor: [lat, lng] }
  const highlightTimerRef = useRef(null);
  const [popoverForm,   setPopoverForm]   = useState(null); // { label, color, opacity }

  // ── Standalone label state (Feature 1) ─────────────────────────────────────
  // addingLabel = "Add Label" tool armed; next map click drops a new label.
  // editingLabel/labelForm = popover state for the currently open label form.
  const [addingLabel,   setAddingLabel]   = useState(false);
  const [editingLabel,  setEditingLabel]  = useState(null); // { id, text, lat, lng, ... }
  const [labelForm,     setLabelForm]     = useState(null);

  // ── Auto Region (geocode) state ────────────────────────────────────────────
  // geocodeOpen = search input visible; geocodeQuery/Results/Busy/Error drive
  // the small inline panel.  Rate limit is enforced client-side via
  // geocodeLastAtRef in addition to the server-side throttle.
  const [geocodeOpen,    setGeocodeOpen]    = useState(false);
  const [geocodeQuery,   setGeocodeQuery]   = useState('');
  const [geocodeBusy,    setGeocodeBusy]    = useState(false);
  const [geocodeResults, setGeocodeResults] = useState(null); // null | [] | [results]
  const [geocodeError,   setGeocodeError]   = useState('');
  const geocodeLastAtRef = useRef(0);

  // ── Refs ───────────────────────────────────────────────────────────────────
  const mapElRef        = useRef(null);
  const mapRef          = useRef(null);
  const clusterRef      = useRef(null);
  const tileLayRef      = useRef(null);
  const markersRef      = useRef({});
  const scrubberRef     = useRef(null);
  const activeIdxRef    = useRef(activeIdx);
  const prevIdxRef      = useRef(activeIdx);
  // Tier 1
  const tileErrCountRef = useRef(0);
  const tileErrWinRef   = useRef(0);
  const offlineDetRef   = useRef(false);
  const recoveryTimRef  = useRef(null);
  const toastTimerRef   = useRef(null);
  // Tier 2
  const routeLineRef    = useRef(null);
  const heatLayersRef   = useRef([]);
  // Tier 3
  const measureGroupRef  = useRef(null);
  // Region drawing
  const drawControlRef   = useRef(null);
  const drawHandlerRef   = useRef(null);  // active L.Draw.Polygon instance
  // While a polygon is being drawn, this holds the latLngs of vertices the
  // user has just undone — so Ctrl/⌘+Shift+Z can re-add them.
  const drawRedoStackRef = useRef([]);
  const regionLayersRef  = useRef({});    // { [id]: L.Polygon }
  const labelLayersRef   = useRef({});    // { [id]: L.Marker } — standalone labels
  const addingLabelRef   = useRef(false); // synced flag for use inside Leaflet click callback
  const deleteModeRef    = useRef(false); // synced for use inside Leaflet callbacks
  const weatherCacheRef  = useRef(new Map());
  const weatherTimRef    = useRef(null);
  const isFirstBuildRef  = useRef(false); // flips to true after the initial fitBounds
  activeIdxRef.current  = activeIdx;

  // ── iOS Safari --vh fix ────────────────────────────────────────────────────
  useEffect(() => {
    function setVh() {
      document.documentElement.style.setProperty('--vh', `${window.innerHeight * 0.01}px`);
    }
    setVh();
    window.addEventListener('resize', setVh);
    window.addEventListener('orientationchange', setVh);
    return () => {
      window.removeEventListener('resize', setVh);
      window.removeEventListener('orientationchange', setVh);
    };
  }, []);

  // ── Toast ──────────────────────────────────────────────────────────────────
  const showToast = useCallback((msg) => {
    setToast(msg);
    setToastVisible(true);
    clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastVisible(false), 3000);
  }, []);

  // ── Tile error / offline fallback ──────────────────────────────────────────
  const attachTileHandlers = useCallback((tileLayer, isFallback = false) => {
    tileLayer.on('tileload', () => {
      if (offlineDetRef.current) {
        offlineDetRef.current = false;
        setOfflineBanner(false);
      }
    });
    tileLayer.on('tileerror', () => {
      const now = Date.now();
      if (now - tileErrWinRef.current > 10000) {
        tileErrWinRef.current   = now;
        tileErrCountRef.current = 0;
      }
      tileErrCountRef.current += 1;
      // OpenTopoMap + Stadia (Stamen) hosts are slower / rate-limited than
      // CARTO and OSM — surface their occasional misses through Leaflet's
      // built-in retry rather than swapping the whole layer to the fallback.
      const threshold = 12;
      if (tileErrCountRef.current < threshold) return;

      if (isFallback) {
        if (!offlineDetRef.current) {
          offlineDetRef.current = true;
          setOfflineBanner(true);
          clearInterval(recoveryTimRef.current);
          recoveryTimRef.current = setInterval(() => {
            fetch('https://tile.openstreetmap.org/0/0/0.png', { cache: 'no-store', mode: 'no-cors' })
              .then(() => {
                clearInterval(recoveryTimRef.current);
                tileErrCountRef.current = 0;
                offlineDetRef.current   = false;
                setOfflineBanner(false);
                setTileStyle(detectDarkTheme() ? 'dark' : 'standard');
              })
              .catch(() => {});
          }, 30000);
        }
      } else {
        tileErrCountRef.current = 0;
        const map = mapRef.current;
        if (!map) return;
        if (tileLayRef.current) map.removeLayer(tileLayRef.current);
        const fb = TILES.fallback;
        const fallbackLayer = L.tileLayer(fb.url, { attribution: fb.attr, maxZoom: 18, subdomains: fb.sub }).addTo(map);
        attachTileHandlers(fallbackLayer, true);
        tileLayRef.current = fallbackLayer;
      }
    });
  }, []);

  // Cleanup on unmount
  useEffect(() => () => {
    clearInterval(recoveryTimRef.current);
    clearTimeout(toastTimerRef.current);
    clearTimeout(weatherTimRef.current);
  }, []);

  // Clamp activeIdx when events shrink
  useEffect(() => {
    if (sorted.length > 0 && activeIdx >= sorted.length) setActiveIdx(sorted.length - 1);
  }, [sorted.length]);

  // ── Initialize Leaflet map ─────────────────────────────────────────────────
  useEffect(() => {
    const el = mapElRef.current;
    if (!el || mapRef.current) return;

    const validCenter = typeof mapCenter?.lat === 'number' && isFinite(mapCenter.lat) &&
                        typeof mapCenter?.lng === 'number' && isFinite(mapCenter.lng);
    const initCenter = validCenter ? [mapCenter.lat, mapCenter.lng] : [20, 0];
    const initZoom   = validCenter ? (mapCenter.zoom || 6) : 2;
    const map = L.map(el, { center: initCenter, zoom: initZoom, zoomControl: false });
    L.control.zoom({ position: 'topright' }).addTo(map);

    const initStyle = detectDarkTheme() ? 'dark' : 'standard';
    const t = TILES[initStyle];
    const tileLayer = L.tileLayer(t.url, { attribution: t.attr, maxZoom: t.maxZoom || 18, subdomains: t.sub }).addTo(map);
    attachTileHandlers(tileLayer, false);
    tileLayRef.current = tileLayer;

    const cluster = L.markerClusterGroup({
      maxClusterRadius: 60,
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      iconCreateFunction: (c) => makeClusterIcon(c.getChildCount(), primary),
    });
    clusterRef.current = cluster;
    map.addLayer(cluster);

    mapRef.current = map;
    requestAnimationFrame(() => map.invalidateSize());
    setMapReady(true);

    return () => {
      clearInterval(recoveryTimRef.current);
      map.remove();
      mapRef.current     = null;
      clusterRef.current = null;
      tileLayRef.current = null;
      markersRef.current = {};
    };
  }, []);

  // ── Build markers ──────────────────────────────────────────────────────────
  useEffect(() => {
    const map     = mapRef.current;
    const cluster = clusterRef.current;
    if (!map || !cluster || !mapReady) return;

    cluster.clearLayers();
    markersRef.current = {};
    const curIdx = activeIdxRef.current;
    // ENH: skip rendering markers entirely when the user has hidden them.
    // Regions / legend / scrubber stay untouched.
    const markersHidden = colors?.showMapMarkers === false;
    if (markersHidden) return;

    sorted.forEach((ev, idx) => {
      if (!hasValidCoords(ev)) return;
      const isActive = idx === curIdx;
      const catColor = categoryColor(ev, primary);
      // mapIcon === 'pin' or unset → traditional small pin; any other valid
      // key from MAP_ICONS renders the 32px circular emoji badge.
      const iconKey = ev.mapIcon && MAP_ICONS[ev.mapIcon] ? ev.mapIcon : null;
      const useBadge = iconKey && iconKey !== 'pin';
      const badgeColor = ev.color || catColor || '#C9A96E';
      const sizeKey = ev.mapMarkerSize && BADGE_SIZES[ev.mapMarkerSize] ? ev.mapMarkerSize : 'medium';
      let marker;
      try {
        marker = L.marker([ev.location.lat, ev.location.lng], {
          icon: useBadge
            ? makeIconBadge(iconKey, badgeColor, isActive, sizeKey)
            : makePinIcon(isActive, primary, secondary, catColor),
        });
      } catch (err) {
        console.warn('[MapTimeline] Marker creation failed:', ev.id, err);
        return;
      }

      marker.bindTooltip(ev.title, { permanent: false, direction: 'top', className: 'mp-tooltip' });

      const photo   = ev.photo || ev.photos?.[0];
      const dateStr = formatDate(ev.startDate);
      marker.bindPopup(
        `<div class="mp-popup">
          ${photo ? `<div class="mp-popup-img-wrap"><img src="${photo}" alt="" class="mp-popup-img" loading="lazy"/></div>` : ''}
          <div class="mp-popup-body">
            <div class="mp-popup-title">${ev.title}</div>
            <div class="mp-popup-date">${dateStr}</div>
            ${ev.category ? `<div class="mp-popup-cat" style="background:${catColor}22;color:${catColor};border:1px solid ${catColor}44;">${ev.category}</div>` : ''}
            <button class="mp-popup-view" data-ev-idx="${idx}">View Details</button>
          </div>
        </div>`,
        { className: 'mp-popup-pane', maxWidth: 260, minWidth: 180 }
      );

      marker.on('popupopen', () => {
        const btn = marker.getPopup()?.getElement()?.querySelector('.mp-popup-view');
        if (btn) btn.onclick = () => { onEventClick?.(ev); marker.closePopup(); };
      });
      marker.on('click', () => {
        setActiveIdx(idx);
        // ENH 2 (multi-region): highlight every region whose linked-event
        // list contains this event.
        const linkedIds = (colors?.mapRegions || [])
          .filter(r => getRegionLinkedIds(r).includes(ev.id))
          .map(r => r.id);
        if (linkedIds.length) {
          setHighlightedRegionIds(linkedIds);
          clearTimeout(highlightTimerRef.current);
          highlightTimerRef.current = setTimeout(() => setHighlightedRegionIds([]), 900);
        }
      });

      cluster.addLayer(marker);
      markersRef.current[ev.id] = marker;
    });

    // On first load: fit all located events AND region vertices in view
    // instead of jumping to one pin.  Subsequent rebuilds (event edits) skip
    // so the user's current pan/zoom is preserved.
    const pts = sorted.filter(hasValidCoords).map(e => [e.location.lat, e.location.lng]);
    regions.forEach(region => {
      if (!Array.isArray(region.coordinates)) return;
      region.coordinates.forEach(c => {
        if (Array.isArray(c) && isFinite(c[0]) && isFinite(c[1])) pts.push([c[0], c[1]]);
      });
    });
    if (pts.length > 0) {
      if (!isFirstBuildRef.current) {
        isFirstBuildRef.current = true;
        try {
          if (pts.length === 1) {
            map.setView(pts[0], 8, { animate: false });
          } else {
            map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], animate: false });
          }
        } catch (err) {
          console.warn('[MapTimeline] fitBounds/setView failed:', err);
        }
      }
    }
  }, [mapReady, sorted, primary, secondary, regions, colors?.showMapMarkers]);

  // ── Update pin icons on activeIdx change ───────────────────────────────────
  useEffect(() => {
    if (!mapReady) return;
    const markers = markersRef.current;
    const buildIcon = (ev, isActive) => {
      const catColor = categoryColor(ev, primary);
      const iconKey = ev.mapIcon && MAP_ICONS[ev.mapIcon] ? ev.mapIcon : null;
      const sizeKey = ev.mapMarkerSize && BADGE_SIZES[ev.mapMarkerSize] ? ev.mapMarkerSize : 'medium';
      if (iconKey && iconKey !== 'pin') {
        const badgeColor = ev.color || catColor || '#C9A96E';
        return makeIconBadge(iconKey, badgeColor, isActive, sizeKey);
      }
      return makePinIcon(isActive, primary, secondary, catColor);
    };
    const prev    = sorted[prevIdxRef.current];
    const current = sorted[activeIdx];
    if (prev    && markers[prev.id])    markers[prev.id].setIcon(buildIcon(prev, false));
    if (current && markers[current.id]) markers[current.id].setIcon(buildIcon(current, true));
    prevIdxRef.current = activeIdx;
  }, [activeIdx, mapReady, sorted, primary, secondary]);

  // ── Fly to active event ────────────────────────────────────────────────────
  // Skip on first ready (fitBounds shows all events) and skip when only `sorted`
  // changes (event edits) — only fly when the user actually navigates to a new
  // event via card click, marker click, arrow keys, or slideshow advance.
  const lastFlyIdxRef = useRef(null);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (lastFlyIdxRef.current === null) { lastFlyIdxRef.current = activeIdx; return; }
    if (lastFlyIdxRef.current === activeIdx) return;
    lastFlyIdxRef.current = activeIdx;
    const ev = sorted[activeIdx];
    if (!hasValidCoords(ev)) return;
    // During slideshow play, zoom to a regional level — close enough to
    // pick out the location but loose enough that you still see surrounding
    // geography. (City-level zoom 12 was too tight per UX feedback.)
    const baseZoom = ev.location.zoom || 5;
    const targetZoom = playing ? Math.max(baseZoom, 9) : baseZoom;
    try {
      map.flyTo([ev.location.lat, ev.location.lng], targetZoom, { duration: 1.2, easeLinearity: 0.5 });
    } catch (err) {
      console.warn('[MapTimeline] flyTo failed:', err);
    }
  }, [activeIdx, mapReady, sorted, playing]);

  // ── Switch tile layers ─────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (tileLayRef.current) map.removeLayer(tileLayRef.current);
    const t = TILES[tileStyle];
    const newLayer = L.tileLayer(t.url, { attribution: t.attr, maxZoom: t.maxZoom || 18, subdomains: t.sub }).addTo(map);
    attachTileHandlers(newLayer, false);
    tileLayRef.current = newLayer;
    tileErrCountRef.current = 0;
    tileErrWinRef.current   = 0;
  }, [tileStyle, mapReady]);

  // ── Auto-sync tile with app theme (only if user hasn't picked a style) ────
  // Once colors.mapStyle is set the user has made an explicit choice — leave
  // it alone on theme toggle so their preference sticks.
  useEffect(() => {
    if (colors?.mapStyle) return;
    const observer = new MutationObserver(() => {
      setTileStyle(prev =>
        (prev !== 'dark' && prev !== 'standard') ? prev : detectDarkTheme() ? 'dark' : 'standard'
      );
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme', 'class'] });
    return () => observer.disconnect();
  }, [colors?.mapStyle]);

  // ── Route polyline ─────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (routeLineRef.current) { map.removeLayer(routeLineRef.current); routeLineRef.current = null; }
    if (!showRoute) return;
    const pts = sorted.filter(hasValidCoords).map(e => [e.location.lat, e.location.lng]);
    if (pts.length < 2) return;
    routeLineRef.current = L.polyline(pts, { color: primary, weight: 2.5, opacity: 0.65, dashArray: '7 5' }).addTo(map);
  }, [showRoute, mapReady, sorted, primary]);

  // ── Heatmap circles ────────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    heatLayersRef.current.forEach(c => map.removeLayer(c));
    heatLayersRef.current = [];
    if (!showHeatmap) return;
    sorted.filter(hasValidCoords).forEach(ev => {
      const col = categoryColor(ev, primary);
      const c   = L.circleMarker([ev.location.lat, ev.location.lng], {
        radius: 30, fillColor: col, fillOpacity: 0.18, stroke: false, interactive: false, pane: 'shadowPane',
      }).addTo(map);
      heatLayersRef.current.push(c);
    });
  }, [showHeatmap, mapReady, sorted, primary]);

  // Latest regions/persistRegions refs so the reshape effect doesn't tear
  // down and re-enable the vertex handles on every region state change.
  const regionsRef        = useRef(regions);
  const persistRegionsRef = useRef(null);
  useEffect(() => { regionsRef.current = regions; }, [regions]);

  // Latest labels mirror for the same reason (drag handlers read this).
  const labelsRef        = useRef(labels);
  const persistLabelsRef = useRef(null);
  useEffect(() => { labelsRef.current = labels; }, [labels]);

  // ── Map edit history (local, session-only, max 50 entries) ────────────────
  // Stack holds {regions, labels} snapshots; idx points at the currently-applied
  // entry. Both regions and standalone labels share one undo stack so Ctrl/⌘+Z
  // walks back the most recent map edit regardless of which entity it touched.
  // Saving the timeline does NOT clear history — the user can still undo
  // unsaved in-session edits after a save (per spec).
  const historyRef = useRef({ stack: [], idx: -1, initialized: false });
  const [historyTick, setHistoryTick] = useState(0);

  const pushHistory = useCallback((snapshot) => {
    const h = historyRef.current;
    // Drop redo branch if user is branching from an undone state
    h.stack = h.stack.slice(0, h.idx + 1);
    h.stack.push(snapshot);
    if (h.stack.length > 50) h.stack.shift();
    h.idx = h.stack.length - 1;
    setHistoryTick(t => t + 1);
  }, []);

  // History is seeded lazily inside persistRegions on the first edit — that
  // way we capture the state right before the user's first action, even if
  // the colors prop hadn't loaded yet at component mount.

  // ── Persist mapRegions when callback wired ─────────────────────────────────
  // Property-level history: instead of pushing one snapshot per save, we diff
  // the previous and next region arrays and push one intermediate snapshot
  // per change.  That way each field edit (color, label, opacity, border,
  // fill, visibility window, label size, linked event, single-vertex drag),
  // each Add, and each Delete is its own Ctrl/⌘+Z step.
  const PROP_FIELDS = useMemo(() => [
    'label', 'color', 'opacity',
    'borderStyle', 'borderThickness',
    'fillStyle',
    'visibleFrom', 'visibleUntil',
    'labelSize', 'linkedEventIds',
    'coordinates',
  ], []);

  // Label-property fields that each get their own undo step.
  const LABEL_PROP_FIELDS = useMemo(() => [
    'text', 'lat', 'lng',
    'fontSize', 'textColor',
    'backgroundColor', 'backgroundOpacity', 'showBackground',
  ], []);

  // Seed history once on the first edit so the snapshot before any action is
  // always at idx 0.  Both persistRegions/persistLabels call this helper.
  const ensureHistorySeeded = useCallback(() => {
    if (!historyRef.current.initialized) {
      historyRef.current = {
        stack: [{ regions: regionsRef.current, labels: labelsRef.current }],
        idx: 0,
        initialized: true,
      };
      setHistoryTick(t => t + 1);
    }
  }, []);

  const persistRegions = useCallback((next) => {
    const prev = regionsRef.current;
    const labelsSnap = labelsRef.current;
    ensureHistorySeeded();
    // 1. Deletions (one snapshot per removed region)
    let cur = prev;
    for (const r of prev) {
      if (!next.find(n => n.id === r.id)) {
        cur = cur.filter(c => c.id !== r.id);
        pushHistory({ regions: cur, labels: labelsSnap });
      }
    }
    // 2. Field-level edits on regions present in both
    for (const r of next) {
      const prevR = prev.find(p => p.id === r.id);
      if (!prevR) continue; // additions handled below
      for (const f of PROP_FIELDS) {
        const a = prevR[f];
        const b = r[f];
        const same = a === b ||
          (Array.isArray(a) && Array.isArray(b) && JSON.stringify(a) === JSON.stringify(b));
        if (!same) {
          cur = cur.map(c => c.id === r.id ? { ...c, [f]: b } : c);
          pushHistory({ regions: cur, labels: labelsSnap });
        }
      }
    }
    // 3. Additions (one snapshot per new region)
    for (const r of next) {
      if (!prev.find(p => p.id === r.id)) {
        cur = [...cur, r];
        pushHistory({ regions: cur, labels: labelsSnap });
      }
    }
    if (onColorsChange) onColorsChange({ mapRegions: next });
  }, [onColorsChange, pushHistory, PROP_FIELDS, ensureHistorySeeded]);
  useEffect(() => { persistRegionsRef.current = persistRegions; }, [persistRegions]);

  // Same property-level diffing as persistRegions, but for standalone labels.
  // Each delete/field-change/add becomes its own undo step.
  const persistLabels = useCallback((next) => {
    const prev = labelsRef.current;
    const regionsSnap = regionsRef.current;
    ensureHistorySeeded();
    let cur = prev;
    // 1. Deletions
    for (const l of prev) {
      if (!next.find(n => n.id === l.id)) {
        cur = cur.filter(c => c.id !== l.id);
        pushHistory({ regions: regionsSnap, labels: cur });
      }
    }
    // 2. Field-level edits
    for (const l of next) {
      const prevL = prev.find(p => p.id === l.id);
      if (!prevL) continue;
      for (const f of LABEL_PROP_FIELDS) {
        if (prevL[f] !== l[f]) {
          cur = cur.map(c => c.id === l.id ? { ...c, [f]: l[f] } : c);
          pushHistory({ regions: regionsSnap, labels: cur });
        }
      }
    }
    // 3. Additions
    for (const l of next) {
      if (!prev.find(p => p.id === l.id)) {
        cur = [...cur, l];
        pushHistory({ regions: regionsSnap, labels: cur });
      }
    }
    if (onColorsChange) onColorsChange({ mapLabels: next });
  }, [onColorsChange, pushHistory, LABEL_PROP_FIELDS, ensureHistorySeeded]);
  useEffect(() => { persistLabelsRef.current = persistLabels; }, [persistLabels]);

  // Undo/redo walk the shared {regions, labels} stack.  We diff the snapshots
  // and only emit the changed slices to onColorsChange so unrelated parent
  // state isn't churned.
  const applyHistorySnapshot = useCallback((snap, prevSnap) => {
    if (!onColorsChange || !snap) return;
    const patch = {};
    if (snap.regions !== prevSnap?.regions) patch.mapRegions = snap.regions;
    if (snap.labels  !== prevSnap?.labels)  patch.mapLabels  = snap.labels;
    if (Object.keys(patch).length) onColorsChange(patch);
  }, [onColorsChange]);

  const undoRegions = useCallback(() => {
    const h = historyRef.current;
    if (h.idx <= 0) return;
    const prevSnap = h.stack[h.idx];
    h.idx -= 1;
    applyHistorySnapshot(h.stack[h.idx], prevSnap);
    setHistoryTick(t => t + 1);
  }, [applyHistorySnapshot]);

  const redoRegions = useCallback(() => {
    const h = historyRef.current;
    if (h.idx >= h.stack.length - 1) return;
    const prevSnap = h.stack[h.idx];
    h.idx += 1;
    applyHistorySnapshot(h.stack[h.idx], prevSnap);
    setHistoryTick(t => t + 1);
  }, [applyHistorySnapshot]);

  const canUndo = historyRef.current.idx > 0;
  const canRedo = historyRef.current.idx < historyRef.current.stack.length - 1;
  // Reference historyTick so React re-renders when it changes (button state).
  void historyTick;

  // Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z = redo (editor only)
  useEffect(() => {
    if (!canEdit) return;
    const onKey = (e) => {
      const meta = e.ctrlKey || e.metaKey;
      if (!meta) return;
      const isZ = e.key === 'z' || e.key === 'Z';
      if (!isZ) return;

      // While a polygon is being drawn, Ctrl/⌘+Z retracts the last clicked
      // vertex (and Shift+Z re-adds it).  This is what the user expects when
      // mid-draw — distinct from undoing saved region edits.
      const handler = drawHandlerRef.current;
      if (handler) {
        e.preventDefault();
        try {
          if (e.shiftKey) {
            const ll = drawRedoStackRef.current.pop();
            if (ll) handler._addVertex(ll);
          } else {
            const markers = handler._markers || [];
            const last = markers[markers.length - 1];
            if (last) drawRedoStackRef.current.push(last.getLatLng());
            handler.deleteLastVertex();
          }
        } catch {}
        return;
      }

      // Otherwise: property-level region undo/redo.
      // Only skip when the focused element actually has its own native
      // undo (text-like inputs / textareas / contentEditable).  Sliders,
      // color pickers, checkboxes, and buttons don't undo on their own,
      // so we should still handle Ctrl+Z when those are focused.
      const t = e.target;
      const tag = t?.tagName;
      const inputType = (t?.type || '').toLowerCase();
      const isTextLike =
        (tag === 'INPUT' && ['text', 'search', 'email', 'password', 'url', 'tel', 'number'].includes(inputType || 'text')) ||
        tag === 'TEXTAREA' ||
        t?.isContentEditable;
      if (isTextLike) return;
      e.preventDefault();
      if (e.shiftKey) redoRegions(); else undoRegions();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [canEdit, undoRegions, redoRegions]);

  // ── Persist mapStyle when callback wired ───────────────────────────────────
  const applyTileStyle = useCallback((id) => {
    if (!TILES[id]) return;
    setTileStyle(id);
    if (onColorsChange) onColorsChange({ mapStyle: id });
  }, [onColorsChange]);

  // Pull external changes (e.g. picker in the Design tab) into local state
  useEffect(() => {
    const saved = colors?.mapStyle;
    if (saved && TILES[saved] && saved !== tileStyle) setTileStyle(saved);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [colors?.mapStyle]);

  // ── Render persisted region polygons ───────────────────────────────────────
  // Each region renders as TWO layers: the polygon itself (overlayPane, below
  // markers) and a non-interactive label marker pinned to its centroid. The
  // label uses a divIcon so it's a real DOM node we can style consistently —
  // no tooltip show/hide behavior, the pill is always visible.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    // Tear down previous (each entry is { poly, label? })
    Object.values(regionLayersRef.current).forEach(entry => {
      if (entry?.poly)  map.removeLayer(entry.poly);
      if (entry?.label) map.removeLayer(entry.label);
    });
    regionLayersRef.current = {};

    regions.forEach(region => {
      if (!Array.isArray(region.coordinates) || region.coordinates.length < 3) return;
      const fill = region.color || '#C9A96E';
      const opacity = typeof region.opacity === 'number' ? region.opacity : 0.25;
      const borderStyle = region.borderStyle || 'solid';
      const borderThickness = typeof region.borderThickness === 'number'
        ? Math.max(1, Math.min(6, region.borderThickness))
        : 2;
      const fillStyle = region.fillStyle || 'solid';
      const isEditing = editingRegion?.id === region.id;
      const baseWeight = isEditing ? Math.max(borderThickness + 1, 3) : borderThickness;
      const poly = L.polygon(region.coordinates, {
        color:       fill,
        weight:      baseWeight,
        opacity:     1,
        fillColor:   fill,
        // Pattern fills use the region color at full opacity for the lines
        // and let the map show through — so the L.Path fillOpacity is 1; for
        // solid fills we honor the user's opacity slider.
        fillOpacity: fillStyle === 'solid' ? opacity : 1,
        dashArray:   borderDashArray(borderStyle, borderThickness),
        className:   `mp-region${isEditing ? ' mp-region-editing' : ''}`,
      });
      // Permanent label pill at the centroid (arithmetic mean — good enough
      // for convex shapes and what users typically draw on a map).
      let labelMarker = null;
      if (region.label) {
        const n = region.coordinates.length;
        let sumLat = 0, sumLng = 0;
        for (const [lat, lng] of region.coordinates) { sumLat += lat; sumLng += lng; }
        const center = [sumLat / n, sumLng / n];
        const labelText = String(region.label).replace(/[&<>"']/g, ch =>
          ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);
        const sizeKey = ['small', 'medium', 'large'].includes(region.labelSize)
          ? region.labelSize : 'medium';
        labelMarker = L.marker(center, {
          icon: L.divIcon({
            className: '', // suppress default leaflet-div-icon styling
            html: `<span class="mp-region-label mp-region-label-${sizeKey}${isEditing ? ' mp-region-label-active' : ''}">${labelText}</span>`,
            iconSize:   null,
            iconAnchor: null,
          }),
          interactive: false,
          keyboard:    false,
        });
        labelMarker.addTo(map);
      }

      const labelEl = () => labelMarker?.getElement()?.querySelector('.mp-region-label');
      poly.on('mouseover', () => {
        poly.setStyle({ weight: 3 });
        labelEl()?.classList.add('mp-region-label-active');
      });
      poly.on('mouseout', () => {
        poly.setStyle({ weight: editingRegion?.id === region.id ? 3 : 2 });
        if (editingRegion?.id !== region.id) {
          labelEl()?.classList.remove('mp-region-label-active');
        }
      });
      poly.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        if (deleteModeRef.current && canEdit) {
          const next = regions.filter(r => r.id !== region.id);
          persistRegions(next);
          return;
        }
        // ENH 2 (multi-region): if the region has any linked events, bounce
        // every linked marker and pop a preview card listing all of them.
        const linkedIds = getRegionLinkedIds(region);
        if (linkedIds.length) {
          const linkedEvs = linkedIds
            .map(id => sorted.find(s => s.id === id))
            .filter(Boolean);
          linkedEvs.forEach(linkedEv => {
            const markerEl = markersRef.current[linkedEv.id]?.getElement?.();
            if (markerEl) {
              markerEl.classList.remove('mp-marker-bounce');
              // eslint-disable-next-line no-unused-expressions
              markerEl.offsetWidth;
              markerEl.classList.add('mp-marker-bounce');
              setTimeout(() => markerEl.classList.remove('mp-marker-bounce'), 700);
            }
          });
          if (linkedEvs.length) {
            const n = region.coordinates.length;
            let sumLat = 0, sumLng = 0;
            for (const [lat, lng] of region.coordinates) { sumLat += lat; sumLng += lng; }
            setLinkedEventPopup({ events: linkedEvs, anchor: [sumLat / n, sumLng / n] });
          }
        }
      });
      poly.addTo(map);

      // Override the SVG path fill with a pattern when the user picked one.
      // Must happen after addTo(map) — that's when getElement() returns the
      // rendered <path>.
      if (fillStyle !== 'solid') {
        const patternId = ensurePattern(fillStyle, fill);
        const pathEl = poly.getElement();
        if (pathEl) pathEl.setAttribute('fill', `url(#${patternId})`);
      }

      regionLayersRef.current[region.id] = { poly, label: labelMarker };
    });
  }, [regions, mapReady, editingRegion?.id, canEdit, persistRegions]);

  // Keep deleteModeRef in sync so Leaflet click callbacks see the latest value
  useEffect(() => { deleteModeRef.current = deleteMode; }, [deleteMode]);

  // ── Standalone labels: defaults, helpers, render effect ────────────────────
  // Each label is { id, text, lat, lng, fontSize, textColor, backgroundColor,
  // backgroundOpacity (0..1), showBackground (bool) }.
  const LABEL_FONT_PX = useMemo(() => ({ small: 10, medium: 12, large: 16 }), []);

  // Hex (#RRGGBB) → "rgba(r, g, b, a)".  Falls back to the raw input if the
  // string isn't a 6-digit hex (lets us preserve named/custom colors).
  const hexToRgba = useCallback((hex, alpha) => {
    if (typeof hex !== 'string') return hex;
    const m = hex.trim().match(/^#?([0-9a-f]{6})$/i);
    if (!m) return hex;
    const n = parseInt(m[1], 16);
    return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  }, []);

  const buildLabelPatch = useCallback((form) => ({
    text:              (form.text || '').trim().slice(0, 80),
    fontSize:          ['small', 'medium', 'large'].includes(form.fontSize) ? form.fontSize : 'medium',
    textColor:         form.textColor       || '#F0E6CC',
    backgroundColor:   form.backgroundColor || '#1A1408',
    backgroundOpacity: Math.max(0, Math.min(1, typeof form.backgroundOpacity === 'number' ? form.backgroundOpacity : 0.8)),
    showBackground:    form.showBackground !== false,
  }), []);

  // Render every label as a Leaflet marker with a styled HTML divIcon.  In
  // editor mode markers are draggable; we attach a click handler to (re)open
  // the popover form.  Delete-mode click also routes through here.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    Object.values(labelLayersRef.current).forEach(m => {
      try { map.removeLayer(m); } catch {}
    });
    labelLayersRef.current = {};

    const escapeHtml = (s) => String(s).replace(/[&<>"']/g, ch =>
      ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[ch]);

    labels.forEach(lbl => {
      if (typeof lbl?.lat !== 'number' || typeof lbl?.lng !== 'number') return;
      const text  = String(lbl.text || '');
      if (!text) return; // skip empty (shouldn't persist, but guard anyway)
      const sizePx  = LABEL_FONT_PX[lbl.fontSize] || LABEL_FONT_PX.medium;
      const showBg  = lbl.showBackground !== false;
      const bgRgba  = hexToRgba(lbl.backgroundColor || '#1A1408',
        typeof lbl.backgroundOpacity === 'number' ? lbl.backgroundOpacity : 0.8);
      const fg      = lbl.textColor || '#F0E6CC';
      const isEditing = editingLabel?.id === lbl.id;
      const style = [
        `font-size:${sizePx}px`,
        `color:${fg}`,
        showBg ? `background:${bgRgba}` : 'background:transparent',
        showBg ? 'padding:2px 8px' : 'padding:0',
        showBg ? 'border-radius:12px' : '',
        showBg ? 'box-shadow:0 1px 3px rgba(0,0,0,0.4)' : '',
      ].filter(Boolean).join(';');
      const cls = `mp-map-label${isEditing ? ' mp-map-label-active' : ''}`;
      const marker = L.marker([lbl.lat, lbl.lng], {
        icon: L.divIcon({
          className: '',
          html: `<span class="${cls}" style="${style}">${escapeHtml(text)}</span>`,
          iconSize:   null,
          iconAnchor: null,
        }),
        draggable:    !!canEdit,
        interactive:  !!canEdit,
        keyboard:     false,
      });
      marker.addTo(map);

      marker.on('click', (e) => {
        L.DomEvent.stopPropagation(e);
        if (!canEdit) return;
        if (deleteModeRef.current) {
          persistLabelsRef.current?.(labelsRef.current.filter(x => x.id !== lbl.id));
          return;
        }
        // Open the edit popover for this label.  Capture pre-edit snapshot so
        // Cancel restores rather than deleting (deletion is reserved for
        // freshly-placed labels with no original).
        const cur = labelsRef.current.find(x => x.id === lbl.id) || lbl;
        editingLabelOriginalRef.current = cur;
        setEditingLabel(cur);
        setLabelForm({
          text:              cur.text || '',
          fontSize:          cur.fontSize || 'medium',
          textColor:         cur.textColor || '#F0E6CC',
          backgroundColor:   cur.backgroundColor || '#1A1408',
          backgroundOpacity: typeof cur.backgroundOpacity === 'number' ? cur.backgroundOpacity : 0.8,
          showBackground:    cur.showBackground !== false,
        });
      });

      if (canEdit) {
        marker.on('dragend', () => {
          const ll = marker.getLatLng();
          const cur = labelsRef.current.find(x => x.id === lbl.id);
          if (!cur) return;
          if (cur.lat === ll.lat && cur.lng === ll.lng) return;
          const next = labelsRef.current.map(x =>
            x.id === lbl.id ? { ...x, lat: ll.lat, lng: ll.lng } : x
          );
          persistLabelsRef.current?.(next);
        });
      }

      labelLayersRef.current[lbl.id] = marker;
    });
  }, [labels, mapReady, canEdit, editingLabel?.id, LABEL_FONT_PX, hexToRgba]);

  // Sync addingLabel toggle into a ref so the map-click handler reads the
  // latest value without re-binding.
  useEffect(() => { addingLabelRef.current = addingLabel; }, [addingLabel]);

  // "Add Label" tool: when armed, the next click on the map (outside any
  // existing layer) drops a new label and immediately opens its popover.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady || !canEdit) return;
    const onMapClick = (e) => {
      if (!addingLabelRef.current) return;
      // Don't fire while drawing a region or in delete mode
      if (drawHandlerRef.current || deleteModeRef.current) return;
      const newLabel = {
        id: uid(),
        text: 'New label',
        lat:  e.latlng.lat,
        lng:  e.latlng.lng,
        fontSize:          'medium',
        textColor:         '#F0E6CC',
        backgroundColor:   '#1A1408',
        backgroundOpacity: 0.8,
        showBackground:    true,
      };
      persistLabelsRef.current?.([...labelsRef.current, newLabel]);
      setAddingLabel(false);
      setEditingLabel(newLabel);
      setLabelForm({
        text: newLabel.text,
        fontSize:          newLabel.fontSize,
        textColor:         newLabel.textColor,
        backgroundColor:   newLabel.backgroundColor,
        backgroundOpacity: newLabel.backgroundOpacity,
        showBackground:    newLabel.showBackground,
      });
    };
    map.on('click', onMapClick);
    return () => { map.off('click', onMapClick); };
  }, [mapReady, canEdit]);

  // Crosshair cursor while the Add Label tool is armed.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    const container = map.getContainer();
    if (!container) return;
    if (addingLabel) container.style.cursor = 'crosshair';
    else if (!measureMode) container.style.cursor = '';
  }, [addingLabel, measureMode]);

  // Live-apply label form edits — mirrors region liveApply.
  const liveApplyLabel = useCallback((nextForm, { debounce = false } = {}) => {
    if (!editingLabel) return;
    if (liveDebounceRef.current) { clearTimeout(liveDebounceRef.current); liveDebounceRef.current = null; }
    const doApply = () => {
      const clean = buildLabelPatch(nextForm);
      const next  = labelsRef.current.map(x =>
        x.id === editingLabel.id ? { ...x, ...clean } : x
      );
      persistLabelsRef.current?.(next);
    };
    if (debounce) liveDebounceRef.current = setTimeout(doApply, 250);
    else doApply();
  }, [editingLabel, buildLabelPatch]);

  // Save/Cancel for the label popover.  All edits already live-applied; Save
  // just flushes any debounced field and closes.  Cancel for an existing label
  // restores the pre-edit snapshot; for a brand-new label it deletes it.
  const editingLabelOriginalRef = useRef(null);

  const saveLabelForm = useCallback(() => {
    if (liveDebounceRef.current) {
      clearTimeout(liveDebounceRef.current);
      liveDebounceRef.current = null;
      if (editingLabel && labelForm) {
        const clean = buildLabelPatch(labelForm);
        const cur = labelsRef.current.find(x => x.id === editingLabel.id);
        const same = cur && Object.keys(clean).every(k => cur[k] === clean[k]);
        if (!same) {
          const next = labelsRef.current.map(x =>
            x.id === editingLabel.id ? { ...x, ...clean } : x
          );
          persistLabelsRef.current?.(next);
        }
      }
    }
    // Empty text after trim → delete (don't persist a blank label).
    if (editingLabel && labelForm && !(labelForm.text || '').trim()) {
      persistLabelsRef.current?.(labelsRef.current.filter(x => x.id !== editingLabel.id));
    }
    editingLabelOriginalRef.current = null;
    setEditingLabel(null);
    setLabelForm(null);
  }, [editingLabel, labelForm, buildLabelPatch]);

  const cancelLabelForm = useCallback(() => {
    if (liveDebounceRef.current) { clearTimeout(liveDebounceRef.current); liveDebounceRef.current = null; }
    if (editingLabel) {
      if (editingLabelOriginalRef.current) {
        const orig = editingLabelOriginalRef.current;
        const next = labelsRef.current.map(x => x.id === editingLabel.id ? orig : x);
        persistLabelsRef.current?.(next);
      } else {
        // Brand-new label that wasn't there before — delete it on cancel.
        persistLabelsRef.current?.(labelsRef.current.filter(x => x.id !== editingLabel.id));
      }
    }
    editingLabelOriginalRef.current = null;
    setEditingLabel(null);
    setLabelForm(null);
  }, [editingLabel]);

  const deleteLabelFromForm = useCallback(() => {
    if (!editingLabel) return;
    persistLabelsRef.current?.(labelsRef.current.filter(x => x.id !== editingLabel.id));
    editingLabelOriginalRef.current = null;
    setEditingLabel(null);
    setLabelForm(null);
  }, [editingLabel]);

  // ── Auto Region: GeoJSON → app region coordinates ──────────────────────────
  // GeoJSON polygon rings are [lng, lat]; the app stores region coordinates as
  // [lat, lng].  For MultiPolygon we pick the outer ring of whichever polygon
  // has the most vertices (a cheap proxy for largest area — good enough for
  // typical country lookups).  Returns null if no usable ring is found.
  const geojsonToCoords = useCallback((geojson) => {
    if (!geojson || typeof geojson !== 'object') return null;
    let outer = null;
    if (geojson.type === 'Polygon' && Array.isArray(geojson.coordinates)) {
      outer = geojson.coordinates[0]; // first ring is outer
    } else if (geojson.type === 'MultiPolygon' && Array.isArray(geojson.coordinates)) {
      let best = null;
      for (const poly of geojson.coordinates) {
        const ring = poly?.[0];
        if (Array.isArray(ring) && (!best || ring.length > best.length)) best = ring;
      }
      outer = best;
    }
    if (!Array.isArray(outer) || outer.length < 3) return null;
    // GeoJSON closes the ring (first === last); strip the duplicate.
    const last = outer[outer.length - 1];
    const first = outer[0];
    const ring = (last && first && last[0] === first[0] && last[1] === first[1])
      ? outer.slice(0, -1) : outer;
    return ring.map(([lng, lat]) => [lat, lng]);
  }, []);

  // Submit handler for the geocode search.  Throttles to 1 req/sec client-side
  // (the server independently enforces its own 1 rps + 30/min/user cap).
  const submitGeocode = useCallback(async () => {
    const q = (geocodeQuery || '').trim();
    if (!q) return;
    const gap = Date.now() - geocodeLastAtRef.current;
    if (gap < 1000) await new Promise(r => setTimeout(r, 1000 - gap));
    geocodeLastAtRef.current = Date.now();

    setGeocodeBusy(true);
    setGeocodeError('');
    setGeocodeResults(null);
    try {
      const res = await fetch(`/api/geocode?q=${encodeURIComponent(q)}`, {
        credentials: 'include',
      });
      if (res.status === 429) {
        setGeocodeError('Search is busy — please try again in a moment.');
        return;
      }
      if (!res.ok) {
        setGeocodeError('Search failed. Please try again.');
        return;
      }
      const data = await res.json();
      const list = Array.isArray(data?.results) ? data.results : [];
      if (list.length === 0) {
        setGeocodeResults([]);
        setGeocodeError('No boundary found for that name. Try a more specific location or draw it manually.');
        return;
      }
      setGeocodeResults(list);
    } catch (err) {
      setGeocodeError('Could not reach the geocoding service.');
    } finally {
      setGeocodeBusy(false);
    }
  }, [geocodeQuery]);

  // Materialize one of the geocode results into a region and open the popover
  // for the user to customize before saving.
  const applyGeocodeResult = useCallback((result) => {
    const coords = geojsonToCoords(result?.geojson);
    if (!coords) {
      setGeocodeError('No boundary found for that name. Try a more specific location or draw it manually.');
      setGeocodeResults([]);
      return;
    }
    const defaults = {
      label: (geocodeQuery || result.displayName || '').split(',')[0].trim().slice(0, 60),
      color: '#C9A96E', opacity: 0.25,
      borderStyle: 'solid', borderThickness: 2, fillStyle: 'solid',
      visibleFrom: null, visibleUntil: null,
      labelSize: 'medium', linkedEventIds: [],
    };
    const newRegion = { id: uid(), ...defaults, coordinates: coords };
    persistRegions([...regionsRef.current, newRegion]);
    // Fit the map to the new region
    try {
      const map = mapRef.current;
      if (map && coords.length) {
        const bounds = L.latLngBounds(coords.map(([lat, lng]) => L.latLng(lat, lng)));
        map.fitBounds(bounds, { padding: [40, 40], maxZoom: 8 });
      }
    } catch {}
    // Close the search panel and open the region edit popover
    setGeocodeOpen(false);
    setGeocodeQuery('');
    setGeocodeResults(null);
    setGeocodeError('');
    editingOriginalRef.current = null; // sentinel — Cancel deletes (matches the draw-created flow)
    setEditingRegion(newRegion);
    setPopoverForm({
      label:           defaults.label,
      color:           defaults.color,
      opacity:         defaults.opacity,
      borderStyle:     defaults.borderStyle,
      borderThickness: defaults.borderThickness,
      fillStyle:       defaults.fillStyle,
      visibleFrom:     '',
      visibleUntil:    '',
      labelSize:       defaults.labelSize,
      linkedEventIds:  [],
    });
  }, [geojsonToCoords, geocodeQuery, persistRegions]);

  // ENH 2: apply transient highlight to a linked region when its event is
  // clicked.  Bumps border weight to 4px, fillOpacity to 60%, and toggles a
  // .mp-region-pulse class on the SVG path for the CSS scale animation.
  useEffect(() => {
    if (!highlightedRegionIds || highlightedRegionIds.length === 0) return;
    const undos = [];
    highlightedRegionIds.forEach(id => {
      const entry = regionLayersRef.current[id];
      if (!entry?.poly) return;
      const original = {
        weight: entry.poly.options.weight,
        fillOpacity: entry.poly.options.fillOpacity,
      };
      entry.poly.setStyle({ weight: 4, fillOpacity: 0.6 });
      const el = entry.poly.getElement?.();
      if (el) el.classList.add('mp-region-pulse');
      undos.push(() => {
        try { entry.poly.setStyle(original); } catch {}
        if (el) el.classList.remove('mp-region-pulse');
      });
    });
    return () => undos.forEach(fn => fn());
  }, [highlightedRegionIds]);

  // ── Vertex-drag reshape (editor only) ──────────────────────────────────────
  // When a region is opened for editing, enable leaflet-draw's vertex handles
  // on its polygon.  Each drag commit fires the polygon's `edit` event; we
  // capture the new coordinates and push them through persistRegions (which
  // also adds an undo entry).
  //
  // IMPORTANT: include `regions` in the deps.  Every vertex drag triggers
  // persistRegions → onColorsChange → parent re-render → the region-rendering
  // effect tears down the old L.Polygon and creates a new one.  Without
  // re-running here, the freshly-rebuilt polygon never has editing enabled
  // and the user can only drag once per save.  The cleanup safely no-ops on
  // the already-destroyed prior layer thanks to the try/catch around
  // editing.disable().
  useEffect(() => {
    const editId = editingRegion?.id;
    if (!editId || !canEdit) return;
    const entry = regionLayersRef.current[editId];
    const layer = entry?.poly;
    if (!layer) return;
    try { layer.editing && layer.editing.enable(); } catch {}
    const onEdited = () => {
      const current = layer.getLatLngs();
      // Leaflet wraps polygon latlngs in an array of rings; take outer ring
      const ring = Array.isArray(current?.[0]) ? current[0] : current;
      const newCoords = ring.map(ll => [ll.lat, ll.lng]);
      const cur = regionsRef.current.find(r => r.id === editId);
      if (!cur) return;
      const same = Array.isArray(cur.coordinates)
        && cur.coordinates.length === newCoords.length
        && cur.coordinates.every((c, i) => c[0] === newCoords[i][0] && c[1] === newCoords[i][1]);
      if (same) return;
      const next = regionsRef.current.map(r =>
        r.id === editId ? { ...r, coordinates: newCoords } : r
      );
      persistRegionsRef.current?.(next);
    };
    layer.on('edit',           onEdited);
    layer.on('editend',        onEdited);
    layer.on('vertex:dragend', onEdited); // some leaflet-draw versions
    return () => {
      try { layer.editing && layer.editing.disable(); } catch {}
      try { layer.off('edit',           onEdited); } catch {}
      try { layer.off('editend',        onEdited); } catch {}
      try { layer.off('vertex:dragend', onEdited); } catch {}
    };
  }, [editingRegion?.id, canEdit, regions]);

  // ── Start polygon drawing (editor only) ────────────────────────────────────
  // Uses leaflet-draw's L.Draw.Polygon directly rather than the draw toolbar
  // so we can wire our own UI and surface a popover when the polygon closes.
  const startDrawingRegion = useCallback(() => {
    const map = mapRef.current;
    if (!map || !canEdit) return;
    // Cancel any in-flight draw + leave delete mode
    if (drawHandlerRef.current) { try { drawHandlerRef.current.disable(); } catch {} drawHandlerRef.current = null; }
    setDeleteMode(false);
    setDrawMode(true);
    drawRedoStackRef.current = []; // fresh undo-vertex history per draw session
    const handler = new L.Draw.Polygon(map, {
      allowIntersection: false,
      showArea:          false,
      shapeOptions: {
        color:       '#C9A96E',
        weight:      2,
        fillColor:   '#C9A96E',
        fillOpacity: 0.25,
      },
    });
    drawHandlerRef.current = handler;
    handler.enable();
  }, [canEdit]);

  const cancelDrawing = useCallback(() => {
    if (drawHandlerRef.current) {
      try { drawHandlerRef.current.disable(); } catch {}
      drawHandlerRef.current = null;
    }
    drawRedoStackRef.current = [];
    setDrawMode(false);
  }, []);

  // Vertex-level undo/redo while a polygon is being drawn.  Pulls the last
  // marker's latLng off the active L.Draw.Polygon instance, stashes it on
  // a redo stack, then calls deleteLastVertex to retract the line.
  const undoLastVertex = useCallback(() => {
    const handler = drawHandlerRef.current;
    if (!handler) return false;
    try {
      const markers = handler._markers || [];
      const last = markers[markers.length - 1];
      if (last) drawRedoStackRef.current.push(last.getLatLng());
      handler.deleteLastVertex();
    } catch {}
    return true;
  }, []);

  const redoLastVertex = useCallback(() => {
    const handler = drawHandlerRef.current;
    if (!handler) return false;
    const ll = drawRedoStackRef.current.pop();
    if (!ll) return true;
    // L.Draw.Polygon doesn't expose a public addVertex method, so we tap
    // the internal _addVertex hook (stable since Leaflet.Draw 1.0).
    try { handler._addVertex(ll); } catch {}
    return true;
  }, []);

  // Snapshot of the region's state when editing began — used by Cancel to
  // revert all live changes the user has applied during the session.
  const editingOriginalRef = useRef(null);
  // Slider debounce — collapses every continuous range tick into one history
  // entry when the user finally releases the slider.
  const liveDebounceRef = useRef(null);

  // Open the popover for editing an existing region
  const openEditRegion = useCallback((region) => {
    editingOriginalRef.current = region;
    setEditingRegion(region);
    setPopoverForm({
      label:           region.label || '',
      color:           region.color || '#C9A96E',
      opacity:         typeof region.opacity === 'number' ? region.opacity : 0.25,
      borderStyle:     region.borderStyle || 'solid',
      borderThickness: typeof region.borderThickness === 'number' ? region.borderThickness : 2,
      fillStyle:       region.fillStyle || 'solid',
      visibleFrom:     region.visibleFrom || '',
      visibleUntil:    region.visibleUntil || '',
      labelSize:       region.labelSize || 'medium',
      linkedEventIds:  getRegionLinkedIds(region),
    });
    setDeleteMode(false);
    cancelDrawing();
  }, [cancelDrawing]);

  // Build a clean region patch from a popoverForm-shaped object.
  const buildRegionPatch = (form) => ({
    label:           (form.label || '').trim(),
    color:           form.color || '#C9A96E',
    opacity:         Math.max(0.1, Math.min(0.6, form.opacity)),
    borderStyle:     ['solid', 'dashed', 'dotted'].includes(form.borderStyle) ? form.borderStyle : 'solid',
    borderThickness: Math.max(1, Math.min(6, parseInt(form.borderThickness, 10) || 2)),
    fillStyle:       ['solid', 'hatching', 'crosshatch', 'dots'].includes(form.fillStyle) ? form.fillStyle : 'solid',
    visibleFrom:     (form.visibleFrom  || '').trim() || null,
    visibleUntil:    (form.visibleUntil || '').trim() || null,
    labelSize:       ['small', 'medium', 'large'].includes(form.labelSize) ? form.labelSize : 'medium',
    // Many-to-many: store an array of linked event ids.  Drop the legacy
    // singular field so the new shape is canonical.
    linkedEventIds:  Array.isArray(form.linkedEventIds) ? form.linkedEventIds.filter(Boolean) : [],
    linkedEventId:   null,
  });

  // Live-apply the current form state to the region on the map.  Called from
  // each form-input onChange so the user sees the change immediately AND so
  // each field edit becomes its own undoable history step.  `debounce` is
  // used for continuous controls (sliders) to collapse mid-drag ticks into
  // one history entry on release.
  const liveApply = useCallback((nextForm, { debounce = false } = {}) => {
    if (!editingRegion) return;
    if (liveDebounceRef.current) { clearTimeout(liveDebounceRef.current); liveDebounceRef.current = null; }
    const doApply = () => {
      const clean = buildRegionPatch(nextForm);
      const prev  = regionsRef.current;
      const next  = prev.map(r => r.id === editingRegion.id ? { ...r, ...clean } : r);
      persistRegions(next);
    };
    if (debounce) {
      liveDebounceRef.current = setTimeout(doApply, 250);
    } else {
      doApply();
    }
  }, [editingRegion, persistRegions]);

  // Cross-component bridge: the Design tab's "+ Draw New Region" and per-row
  // Edit buttons live outside MapTimeline.  They dispatch a window CustomEvent
  // which we listen for here to invoke the in-component flow.
  useEffect(() => {
    if (!canEdit) return;
    const onDraw = () => startDrawingRegion();
    const onEdit = (e) => {
      const id = e.detail?.id;
      const region = regions.find(r => r.id === id);
      if (region) openEditRegion(region);
    };
    const onDelete = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      // Route through persistRegions so the delete enters the undo stack.
      persistRegions(regionsRef.current.filter(r => r.id !== id));
    };
    window.addEventListener('chronos:mapDrawRegion',   onDraw);
    window.addEventListener('chronos:mapEditRegion',   onEdit);
    window.addEventListener('chronos:mapDeleteRegion', onDelete);
    return () => {
      window.removeEventListener('chronos:mapDrawRegion',   onDraw);
      window.removeEventListener('chronos:mapEditRegion',   onEdit);
      window.removeEventListener('chronos:mapDeleteRegion', onDelete);
    };
  }, [canEdit, startDrawingRegion, openEditRegion, regions, persistRegions]);

  // Wire up the draw-created event once on map ready.
  // We immediately add the new region with default settings via
  // persistRegions (one "add" history entry) and treat it as an existing
  // region from then on — so live-apply works while the user tweaks the
  // popover.  Cancel removes the just-added region; Save just closes the
  // popover.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    const onCreated = (e) => {
      try { drawHandlerRef.current?.disable(); } catch {}
      drawHandlerRef.current = null;
      setDrawMode(false);
      const latlngs = e.layer.getLatLngs()[0] || [];
      const coords = latlngs.map(ll => [ll.lat, ll.lng]);
      map.removeLayer(e.layer);
      const defaults = {
        label: '', color: '#C9A96E', opacity: 0.25,
        borderStyle: 'solid', borderThickness: 2, fillStyle: 'solid',
        visibleFrom: null, visibleUntil: null,
        labelSize: 'medium', linkedEventIds: [],
      };
      const newRegion = { id: uid(), ...defaults, coordinates: coords };
      persistRegions([...regionsRef.current, newRegion]);
      // editingOriginalRef stays null — sentinel that this was a fresh draw,
      // so Cancel deletes the region instead of restoring a snapshot.
      editingOriginalRef.current = null;
      setPendingRegion(null);
      setEditingRegion(newRegion);
      setPopoverForm({
        label: '', color: '#C9A96E', opacity: 0.25,
        borderStyle: 'solid', borderThickness: 2, fillStyle: 'solid',
        visibleFrom: '', visibleUntil: '',
        labelSize: 'medium', linkedEventIds: [],
      });
    };
    map.on(L.Draw.Event.CREATED, onCreated);
    return () => { map.off(L.Draw.Event.CREATED, onCreated); };
  }, [mapReady, persistRegions]);

  // Save: all changes are already live-applied through liveApply (newly
  // drawn regions get added in the draw-created handler too).  This just
  // flushes any pending debounced slider tick and closes the popover.
  const saveRegionForm = useCallback(() => {
    if (!popoverForm) return;
    if (liveDebounceRef.current) {
      clearTimeout(liveDebounceRef.current);
      liveDebounceRef.current = null;
      // Apply the final settled value immediately. Many-to-many: no more
      // dedup of linkedEventIds — multiple regions can share the same event.
      if (editingRegion) {
        const clean = buildRegionPatch(popoverForm);
        const cur = regionsRef.current.find(r => r.id === editingRegion.id);
        const same = cur && Object.keys(clean).every(k => {
          const a = cur[k], b = clean[k];
          return a === b || (Array.isArray(a) && Array.isArray(b) && JSON.stringify(a) === JSON.stringify(b));
        });
        if (!same) {
          const next = regionsRef.current.map(r =>
            r.id === editingRegion.id ? { ...r, ...clean } : r
          );
          persistRegions(next);
        }
      }
    }
    editingOriginalRef.current = null;
    setPendingRegion(null);
    setEditingRegion(null);
    setPopoverForm(null);
  }, [popoverForm, editingRegion, persistRegions]);

  // Cancel:
  //   • Existing region (editingOriginalRef captured a snapshot when the user
  //     opened the popover): re-persist the snapshot to roll back every live
  //     change at once.
  //   • Newly-drawn region (editingOriginalRef === null, sentinel set in the
  //     draw-created handler): remove the region entirely.
  const cancelRegionForm = useCallback(() => {
    if (liveDebounceRef.current) {
      clearTimeout(liveDebounceRef.current);
      liveDebounceRef.current = null;
    }
    if (editingRegion) {
      if (editingOriginalRef.current) {
        const original = editingOriginalRef.current;
        const next = regionsRef.current.map(r => r.id === editingRegion.id ? original : r);
        persistRegions(next);
      } else {
        persistRegions(regionsRef.current.filter(r => r.id !== editingRegion.id));
      }
    }
    editingOriginalRef.current = null;
    setPendingRegion(null);
    setEditingRegion(null);
    setPopoverForm(null);
  }, [editingRegion, persistRegions]);

  const deleteRegionById = useCallback((id) => {
    persistRegions(regions.filter(r => r.id !== id));
    if (editingRegion?.id === id) cancelRegionForm();
  }, [regions, persistRegions, editingRegion?.id, cancelRegionForm]);

  // ── Measurement tool ───────────────────────────────────────────────────────
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;
    if (!measureMode) return;

    map.doubleClickZoom.disable();
    map.getContainer().style.cursor = 'crosshair';

    const onMapClick = (e) => {
      setMeasurePts(pts => [...pts, { lat: e.latlng.lat, lng: e.latlng.lng }]);
    };
    map.on('click', onMapClick);

    return () => {
      map.off('click', onMapClick);
      map.doubleClickZoom.enable();
      map.getContainer().style.cursor = '';
    };
  }, [measureMode, mapReady]);

  // Redraw measurement overlay when points change
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    if (measureGroupRef.current) { map.removeLayer(measureGroupRef.current); measureGroupRef.current = null; }
    if (measurePts.length === 0) return;

    const group = L.layerGroup();

    // Circle at each point
    measurePts.forEach(pt => {
      L.circleMarker([pt.lat, pt.lng], {
        radius: 5, fillColor: '#fff', fillOpacity: 1, color: primary, weight: 2, interactive: false,
      }).addTo(group);
    });

    if (measurePts.length >= 2) {
      L.polyline(measurePts.map(p => [p.lat, p.lng]), {
        color: '#fff', weight: 2, dashArray: '5 3', interactive: false,
      }).addTo(group);

      let cumulative = 0;
      for (let i = 1; i < measurePts.length; i++) {
        const a = measurePts[i - 1];
        const b = measurePts[i];
        const d = kmBetween(a.lat, a.lng, b.lat, b.lng);
        cumulative += d;
        L.marker([(a.lat + b.lat) / 2, (a.lng + b.lng) / 2], {
          icon: L.divIcon({ className: '', html: `<div class="mp-measure-tag">${formatKm(d)}</div>`, iconSize: null, iconAnchor: [0, 0] }),
          interactive: false, zIndexOffset: 500,
        }).addTo(group);
      }

      if (measurePts.length > 2) {
        const last = measurePts[measurePts.length - 1];
        L.marker([last.lat, last.lng], {
          icon: L.divIcon({ className: '', html: `<div class="mp-measure-total">Total: ${formatKm(cumulative)}</div>`, iconSize: null, iconAnchor: [0, 14] }),
          interactive: false, zIndexOffset: 600,
        }).addTo(group);
      }
    }

    group.addTo(map);
    measureGroupRef.current = group;
  }, [measurePts, mapReady, primary]);

  // ESC key: exit measure mode
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'ArrowLeft')  setActiveIdx(i => Math.max(0, i - 1));
      if (e.key === 'ArrowRight') setActiveIdx(i => Math.min(sorted.length - 1, i + 1));
      if (e.key === 'Escape' && measureMode) {
        setMeasureMode(false);
        setMeasurePts([]);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [sorted.length, measureMode]);

  // ── Weather fetch (Open-Meteo, free, no API key) ────────────────────────────
  useEffect(() => {
    const ev = sorted[activeIdx];
    if (!hasValidCoords(ev)) { setWeather(null); return; }

    const lat = ev.location.lat;
    const lng = ev.location.lng;
    const key = `${lat.toFixed(2)},${lng.toFixed(2)}`;

    if (weatherCacheRef.current.has(key)) {
      setWeather(weatherCacheRef.current.get(key));
      return;
    }

    setWeather({ loading: true });
    clearTimeout(weatherTimRef.current);
    weatherTimRef.current = setTimeout(async () => {
      try {
        const res = await fetch(
          `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}&current=temperature_2m,weather_code&forecast_days=1&timezone=auto`,
          { cache: 'default' }
        );
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const temp = Math.round(data.current.temperature_2m);
        const code = data.current.weather_code;
        const [desc, icon] = WMO[code] ?? ['Unknown', '🌡️'];
        const result = { temp, desc, icon, loading: false, error: null };
        weatherCacheRef.current.set(key, result);
        setWeather(result);
      } catch {
        setWeather({ loading: false, error: true });
      }
    }, 600);

    return () => clearTimeout(weatherTimRef.current);
  }, [activeIdx, sorted]);

  // ── Scrubber scroll sync ───────────────────────────────────────────────────
  useEffect(() => {
    const track = scrubberRef.current;
    if (!track) return;
    const card = track.querySelector(`[data-ev-id="${sorted[activeIdx]?.id}"]`);
    card?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIdx, sorted]);

  // ── Auto-show event preview while slideshow is playing ────────────────────
  useEffect(() => {
    if (playing) setShowPreview(true);
  }, [activeIdx, playing]);

  // ── Auto-play ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!playing) { setProgress(0); return; }
    const TICK = 50, TOTAL = SPEEDS[playSpeed].ms;
    let elapsed = 0;
    const id = setInterval(() => {
      elapsed += TICK;
      setProgress((elapsed / TOTAL) * 100);
      if (elapsed >= TOTAL) {
        elapsed = 0;
        setActiveIdx(i => { if (i + 1 >= sorted.length) { setPlaying(false); return 0; } return i + 1; });
      }
    }, TICK);
    return () => clearInterval(id);
  }, [playing, playSpeed, sorted.length]);

  // ── Callbacks ──────────────────────────────────────────────────────────────

  // Fit to Content: zoom + pan to encompass every visible event marker AND
  // every persisted region vertex.  If nothing is on the map, no-op.
  const fitAll = useCallback(() => {
    const map = mapRef.current;
    if (!map) return;
    const pts = [];
    sorted.forEach(ev => { if (hasValidCoords(ev)) pts.push([ev.location.lat, ev.location.lng]); });
    regions.forEach(region => {
      if (!Array.isArray(region.coordinates)) return;
      region.coordinates.forEach(c => {
        if (Array.isArray(c) && isFinite(c[0]) && isFinite(c[1])) pts.push([c[0], c[1]]);
      });
    });
    if (pts.length === 0) return;
    if (pts.length === 1) { try { map.flyTo(pts[0], 8); } catch {} return; }
    try {
      map.fitBounds(L.latLngBounds(pts), { padding: [40, 40], animate: true });
    } catch (err) {
      console.warn('[MapTimeline] fitBounds failed:', err);
    }
  }, [sorted, regions]);

  const handleMapSearch = useCallback(async (e) => {
    e.preventDefault();
    const q = mapSearch.trim();
    if (!q) return;
    setMapSearchBusy(true);
    try {
      const results = await nominatimSearch(q);
      if (results?.[0]) {
        const lat = parseFloat(results[0].lat);
        const lng = parseFloat(results[0].lon);
        if (isFinite(lat) && isFinite(lng)) {
          mapRef.current?.flyTo([lat, lng], 10, { duration: 1.2 });
        }
        setMapSearch('');
      } else {
        showToast('Location not found');
      }
    } catch (err) {
      showToast(err.status === 429 ? err.message : 'Search failed — check your connection');
    } finally {
      setMapSearchBusy(false);
    }
  }, [mapSearch, showToast]);

  const exportMap = useCallback(async (format) => {
    const mapEl = mapElRef.current;
    if (!mapEl || exporting) return;
    setExporting(true);
    showToast(`Preparing ${format.toUpperCase()}…`);
    try {
      const canvas  = await getMapCanvas(mapEl);
      const dataUrl = canvas.toDataURL('image/png');
      if (format === 'png') {
        const a = document.createElement('a');
        a.href     = dataUrl;
        a.download = 'map-timeline.png';
        a.click();
      } else {
        const { width, height } = canvas;
        const pdf = new jsPDF({
          orientation: width > height ? 'landscape' : 'portrait',
          unit:   'px',
          format: [width, height],
        });
        pdf.addImage(dataUrl, 'PNG', 0, 0, width, height);
        pdf.save('map-timeline.pdf');
      }
      showToast(`${format.toUpperCase()} saved`);
    } catch (err) {
      console.error('[MapTimeline] Export failed:', err);
      showToast('Export failed — try again');
    } finally {
      setExporting(false);
    }
  }, [exporting, showToast]);

  const navigate = (dir) => setActiveIdx(i => Math.max(0, Math.min(sorted.length - 1, i + dir)));

  // ── Derived ────────────────────────────────────────────────────────────────

  const eventsWithLoc  = sorted.filter(hasValidCoords);
  const activeEv       = sorted[activeIdx];
  const activeDesc     = activeEv ? stripHtml(activeEv.description || '').slice(0, 180) : '';
  const activePhoto    = activeEv ? (activeEv.photo || activeEv.photos?.[0]) : null;

  const filterLower    = filterQuery.toLowerCase();
  const filteredEvents = filterQuery
    ? sorted.filter(ev => ev.title?.toLowerCase().includes(filterLower) || ev.description?.toLowerCase().includes(filterLower))
    : sorted;

  const routeDist = showRoute && eventsWithLoc.length >= 2 ? routeDistance(sorted) : 0;

  const measureDist = useMemo(() => {
    if (measurePts.length < 2) return 0;
    let d = 0;
    for (let i = 1; i < measurePts.length; i++) {
      d += kmBetween(measurePts[i-1].lat, measurePts[i-1].lng, measurePts[i].lat, measurePts[i].lng);
    }
    return d;
  }, [measurePts]);

  const displayTemp = weather && !weather.loading && !weather.error
    ? (weatherUnit === 'C' ? `${weather.temp}°C` : `${Math.round(weather.temp * 9/5 + 32)}°F`)
    : null;

  if (sorted.length === 0) {
    return (
      <div className="mp-empty-root">
        <div className="mp-empty-msg">No events yet. Add events to see them on the map.</div>
      </div>
    );
  }

  const embedUrl  = timeline ? `${window.location.origin}/embed/${timeline.id}` : '';
  const safeEmbedTitle = (timeline?.title || 'Timeline').replace(/"/g, '&quot;');
  const embedCode = `<iframe\n  src="${embedUrl}"\n  width="100%"\n  height="500"\n  style="border:none;border-radius:12px;"\n  title="${safeEmbedTitle}"\n  allowfullscreen\n></iframe>`;

  return (
    <>
    <div className="mp-root" style={{ '--mp-primary': primary, '--mp-secondary': secondary }}>

      {/* ── Map zone ────────────────────────────────────────────────────────── */}
      <div className="mp-map-zone">
        <div ref={mapElRef} className="mp-map-el" />

        {/* Map Title + Date Range card (top-center, both fields optional) */}
        {((colors?.mapTitle && colors.mapTitle.trim()) || (colors?.mapDateRange && colors.mapDateRange.trim())) && (
          <div className="mp-title-card" role="heading" aria-level="2">
            {colors?.mapTitle && colors.mapTitle.trim() && (
              <div className="mp-title-card-title">{colors.mapTitle}</div>
            )}
            {colors?.mapDateRange && colors.mapDateRange.trim() && (
              <div className="mp-title-card-range">{colors.mapDateRange}</div>
            )}
          </div>
        )}

        {/* ENH 5: Map legend (position+style configurable via colors.mapLegendSettings) */}
        {colors?.showMapLegend && (() => {
          const ls = colors?.mapLegendSettings || {};
          const showRegionsRow = ls.showRegionEntries !== false;
          const showIconsRow   = ls.showMarkerEntries !== false;
          const showLabelsRow  = ls.showLabelEntries  !== false;
          const legendRegions = showRegionsRow ? regions.filter(r => r.label && r.label.trim()) : [];
          const legendLabels  = showLabelsRow  ? labels.filter(l => l.text && String(l.text).trim()) : [];
          // Find unique mapIcon types used on the timeline (excluding default pin)
          const usedIcons = [];
          if (showIconsRow) {
            const seen = new Set();
            sorted.forEach(ev => {
              const k = ev.mapIcon;
              if (k && k !== 'pin' && MAP_ICONS[k] && !seen.has(k)) {
                seen.add(k);
                usedIcons.push(k);
              }
            });
          }
          const iconLabels = colors?.mapIconLabels || {};
          const customEntries = Array.isArray(ls.customEntries)
            ? ls.customEntries.filter(e => e && (e.text || '').trim()).slice(0, 10)
            : [];
          if (legendRegions.length === 0 && usedIcons.length === 0 && legendLabels.length === 0 && customEntries.length === 0) return null;

          // Style + class from settings, with sensible defaults
          const pos       = ['tl', 'tr', 'bl', 'br'].includes(ls.position) ? ls.position : 'tl';
          const sz        = ['small', 'medium', 'large'].includes(ls.fontSize) ? ls.fontSize : 'medium';
          const title     = (ls.title || 'Legend').toString().slice(0, 40);
          const bg        = hexToRgba(ls.backgroundColor || '#1A1408',
            typeof ls.backgroundOpacity === 'number' ? ls.backgroundOpacity : 0.9);
          const fg        = ls.textColor   || '#F0E6CC';
          const bd        = ls.borderColor || '#C9A96E';
          const legendStyle = {
            background: bg,
            color: fg,
            borderColor: bd,
          };

          return (
            <div
              className={`mp-legend mp-legend-pos-${pos} mp-legend-sz-${sz}${legendCollapsed ? ' mp-legend-collapsed' : ''}`}
              style={legendStyle}
            >
              {legendCollapsed ? (
                <button
                  type="button"
                  className="mp-legend-expand"
                  onClick={() => setLegendCollapsed(false)}
                  title="Expand legend"
                  aria-label="Expand legend"
                  style={{ color: fg, borderColor: bd }}
                >
                  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2">
                    <line x1="4" y1="6"  x2="20" y2="6"  />
                    <line x1="4" y1="12" x2="20" y2="12" />
                    <line x1="4" y1="18" x2="14" y2="18" />
                  </svg>
                </button>
              ) : (
                <>
                  <div className="mp-legend-header">
                    <span className="mp-legend-title" style={{ color: bd }}>{title}</span>
                    <button
                      type="button"
                      className="mp-legend-collapse"
                      onClick={() => setLegendCollapsed(true)}
                      title="Collapse legend"
                      aria-label="Collapse legend"
                      style={{ color: fg, borderColor: bd }}
                    >−</button>
                  </div>
                  <div className="mp-legend-body">
                    {legendRegions.map(r => (
                      <div key={r.id} className="mp-legend-item">
                        <span className="mp-legend-swatch" style={{ background: r.color || '#C9A96E', borderColor: r.color || '#C9A96E' }} />
                        <span className="mp-legend-label">{r.label}</span>
                      </div>
                    ))}
                    {usedIcons.map(k => (
                      <div key={`icon-${k}`} className="mp-legend-item">
                        <span className="mp-legend-icon" aria-hidden="true">{MAP_ICONS[k].emoji}</span>
                        <span className="mp-legend-label">{iconLabels[k] || MAP_ICONS[k].label}</span>
                      </div>
                    ))}
                    {legendLabels.length > 0 && (legendRegions.length > 0 || usedIcons.length > 0) && (
                      <div className="mp-legend-divider" aria-hidden="true" />
                    )}
                    {legendLabels.length > 0 && (
                      <div className="mp-legend-section-title">Labels</div>
                    )}
                    {legendLabels.map(l => (
                      <div key={`lbl-${l.id}`} className="mp-legend-item">
                        <span className="mp-legend-icon" aria-hidden="true" style={{
                          color: l.textColor || '#F0E6CC',
                          background: l.showBackground !== false ? hexToRgba(l.backgroundColor || '#1A1408', typeof l.backgroundOpacity === 'number' ? l.backgroundOpacity : 0.8) : 'transparent',
                          padding: '1px 4px',
                          borderRadius: 4,
                          fontSize: 10,
                          fontWeight: 600,
                        }}>T</span>
                        <span className="mp-legend-label">{l.text}</span>
                      </div>
                    ))}
                    {customEntries.length > 0 && (legendRegions.length > 0 || usedIcons.length > 0 || legendLabels.length > 0) && (
                      <div className="mp-legend-divider" aria-hidden="true" />
                    )}
                    {customEntries.map((entry, i) => (
                      <div key={`ce-${i}`} className="mp-legend-item">
                        {entry.icon
                          ? <span className="mp-legend-icon" aria-hidden="true">{entry.icon}</span>
                          : <span className="mp-legend-swatch" style={{ background: entry.color || bd, borderColor: entry.color || bd }} />}
                        <span className="mp-legend-label">{entry.text}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          );
        })()}

        {/* Offline banner */}
        {offlineBanner && (
          <div className="mp-offline-banner" role="alert">
            <span>Map tiles unavailable — check your connection. Retrying automatically.</span>
            <button className="mp-offline-dismiss" onClick={() => setOfflineBanner(false)} aria-label="Dismiss">✕</button>
          </div>
        )}

        {/* Measure mode banner */}
        {measureMode && (
          <div className="mp-measure-banner" role="status">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="13" height="13"
              strokeLinecap="round" strokeLinejoin="round">
              <path d="M2 12h4M18 12h4M12 2v4M12 18v4" />
              <rect x="6" y="6" width="12" height="12" rx="2" />
            </svg>
            {measurePts.length === 0
              ? 'Click on the map to start measuring'
              : measurePts.length === 1
              ? 'Click again to add more points'
              : `${measurePts.length} points · ${formatKm(measureDist)}`
            }
            {measurePts.length > 0 && (
              <button className="mp-measure-clear" onClick={() => setMeasurePts([])} title="Clear points">Clear</button>
            )}
            <button className="mp-measure-done" onClick={() => { setMeasureMode(false); setMeasurePts([]); }}>Done</button>
          </div>
        )}

        {/* Toast */}
        <div className={`mp-toast${toastVisible ? ' mp-toast-visible' : ''}`} role="status" aria-live="polite">
          {toast}
        </div>

        {/* Event preview panel */}
        {activeEv && showPreview && (
          <div className="mp-preview">
            <button className="mp-preview-close" onClick={() => setShowPreview(false)} aria-label="Close preview">✕</button>
            {activePhoto && <img src={activePhoto} alt="" className="mp-preview-img" />}
            {(() => {
              const src = toYtEmbedUrl(activeEv.videoUrl);
              if (!src) return null;
              return (
                <div style={{ position: 'relative', width: '100%', paddingTop: '56.25%', overflow: 'hidden' }}>
                  <iframe
                    src={src}
                    title={activeEv.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 'none' }}
                  />
                </div>
              );
            })()}
            <div className="mp-preview-body">
              <div className="mp-preview-date">{formatDate(activeEv.startDate)}</div>
              <div className="mp-preview-title">{activeEv.title}</div>

              {activeEv.category && (
                <div className="mp-preview-cat" style={{
                  background: `${categoryColor(activeEv, primary)}22`,
                  color:       categoryColor(activeEv, primary),
                  border:     `1px solid ${categoryColor(activeEv, primary)}44`,
                }}>
                  {activeEv.category}
                </div>
              )}

              {activeEv.location?.name && (
                <div className="mp-preview-loc">
                  <svg viewBox="0 0 24 24" fill="currentColor" width="10" height="10" style={{ flexShrink: 0, marginTop: 1 }}>
                    <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
                  </svg>
                  {activeEv.location.name}
                </div>
              )}

              {/* Weather */}
              {weather && !weather.error && hasValidCoords(activeEv) && (
                <div className="mp-weather">
                  {weather.loading ? (
                    <span className="mp-weather-loading">Loading weather…</span>
                  ) : (
                    <>
                      <span className="mp-weather-icon">{weather.icon}</span>
                      <span className="mp-weather-desc">{weather.desc}</span>
                      <button
                        className="mp-weather-temp"
                        onClick={() => setWeatherUnit(u => u === 'C' ? 'F' : 'C')}
                        title="Toggle °C / °F"
                      >
                        {displayTemp}
                      </button>
                    </>
                  )}
                </div>
              )}

              {activeDesc && (
                <p className="mp-preview-desc">{activeDesc}{activeDesc.length === 180 ? '…' : ''}</p>
              )}
              <button className="mp-preview-view" onClick={() => onEventClick?.(activeEv)} style={{ background: primary }}>
                View Details
              </button>
            </div>
          </div>
        )}

        {activeEv && !showPreview && (
          <button className="mp-preview-reopen" onClick={() => setShowPreview(true)} title="Show event preview" style={{ borderColor: primary, color: primary }}>
            <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
            </svg>
            Info
          </button>
        )}

        {/* Controls toggle — mobile only, hidden on desktop via CSS */}
        <button
          className={`mp-controls-toggle${showControls ? ' mp-controls-toggle-active' : ''}`}
          onClick={() => setShowControls(v => !v)}
          aria-label={showControls ? 'Hide map controls' : 'Show map controls'}
          title={showControls ? 'Hide controls' : 'Show controls'}
          style={{ borderColor: showControls ? primary : undefined, color: showControls ? primary : undefined }}
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
            strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
            <line x1="4" y1="6"  x2="20" y2="6"  />
            <line x1="4" y1="12" x2="14" y2="12" />
            <line x1="4" y1="18" x2="17" y2="18" />
          </svg>
        </button>

        {/* Map controls cluster — bottom-left */}
        {showControls && <div className="mp-map-controls">
          {(eventsWithLoc.length > 0 || regions.length > 0) && (
            <button className="mp-ctrl-btn" onClick={fitAll} title="Zoom to show all events and regions">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
              </svg>
              Fit
            </button>
          )}

          {eventsWithLoc.length >= 2 && (
            <button
              className={`mp-ctrl-btn${showRoute ? ' mp-ctrl-active' : ''}`}
              onClick={() => setShowRoute(r => !r)}
              style={showRoute ? { borderColor: primary, color: primary } : {}}
              title="Toggle route line"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <path d="M3 17c3-3 6-3 9 0s6 3 9 0" />
                <circle cx="3" cy="17" r="1.5" fill="currentColor" stroke="none" />
                <circle cx="21" cy="17" r="1.5" fill="currentColor" stroke="none" />
              </svg>
              Route
              {showRoute && routeDist > 0 && <span className="mp-ctrl-badge">{formatKm(routeDist)}</span>}
            </button>
          )}

          {eventsWithLoc.length > 0 && (
            <button
              className={`mp-ctrl-btn${showHeatmap ? ' mp-ctrl-active' : ''}`}
              onClick={() => setShowHeatmap(h => !h)}
              style={showHeatmap ? { borderColor: primary, color: primary } : {}}
              title="Toggle heatmap"
            >
              <svg viewBox="0 0 24 24" fill="currentColor" width="12" height="12">
                <circle cx="12" cy="12" r="4" opacity="0.9" />
                <circle cx="12" cy="12" r="8" opacity="0.35" />
                <circle cx="12" cy="12" r="11" opacity="0.15" />
              </svg>
              Heat
            </button>
          )}

          {/* Show / hide event markers (regions + scrubber + legend stay) */}
          <button
            className={`mp-ctrl-btn${colors?.showMapMarkers !== false ? ' mp-ctrl-active' : ''}`}
            onClick={() => {
              if (onColorsChange) onColorsChange({ showMapMarkers: !(colors?.showMapMarkers !== false) });
            }}
            style={colors?.showMapMarkers !== false ? { borderColor: primary, color: primary } : {}}
            title={colors?.showMapMarkers !== false ? 'Hide event markers' : 'Show event markers'}
            aria-pressed={colors?.showMapMarkers !== false}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" />
              <circle cx="12" cy="9" r="2.5" />
            </svg>
            Markers
          </button>

          <button
            className={`mp-ctrl-btn${measureMode ? ' mp-ctrl-active' : ''}`}
            onClick={() => {
              if (measureMode) { setMeasureMode(false); setMeasurePts([]); }
              else setMeasureMode(true);
            }}
            style={measureMode ? { borderColor: primary, color: primary } : {}}
            title="Measure distances on the map (Esc to cancel)"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M2 12h20M5 8l-3 4 3 4M19 8l3 4-3 4" />
            </svg>
            Measure
          </button>

          {/* Export */}
          <button
            className="mp-ctrl-btn"
            onClick={() => exportMap('png')}
            disabled={exporting}
            title="Export map as PNG image"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <rect x="3" y="3" width="18" height="18" rx="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <path d="M21 15l-5-5L5 21" />
            </svg>
            {exporting ? 'Exporting…' : 'PNG'}
          </button>

          <button
            className="mp-ctrl-btn"
            onClick={() => exportMap('pdf')}
            disabled={exporting}
            title="Export map as PDF"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
              strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
              <polyline points="14 2 14 8 20 8" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="15" y2="17" />
            </svg>
            PDF
          </button>

          {!isEmbed && timeline && (
            <button
              className="mp-ctrl-btn"
              onClick={() => timeline.privacy === 'public' && setEmbedOpen(true)}
              disabled={timeline.privacy !== 'public'}
              title={timeline.privacy === 'public' ? 'Get embed code' : 'Make this timeline public to enable embedding'}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
              </svg>
              Embed
            </button>
          )}
        </div>}

        {/* Map location search */}
        <form className="mp-map-search" onSubmit={handleMapSearch}>
          <input
            className="mp-map-search-input"
            type="text"
            placeholder="Jump to location…"
            value={mapSearch}
            onChange={e => setMapSearch(e.target.value)}
            aria-label="Search for a map location"
          />
          <button
            type="submit"
            className="mp-map-search-btn"
            disabled={mapSearchBusy || !mapSearch.trim()}
            aria-label="Search"
          >
            {mapSearchBusy ? <span className="mp-search-spinner" /> : (
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
                <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
            )}
          </button>
        </form>

        {/* Region drawing toolbar — editor only */}
        {canEdit && !isEmbed && (
          <div className="mp-region-toolbar" role="toolbar" aria-label="Region tools">
            <button
              type="button"
              className="mp-region-btn"
              onClick={undoRegions}
              disabled={!canUndo}
              title="Undo last region change (Ctrl/⌘+Z)"
              aria-label="Undo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <path d="M3 7v6h6" />
                <path d="M3 13l4-4a8 8 0 0 1 13 6" />
              </svg>
            </button>
            <button
              type="button"
              className="mp-region-btn"
              onClick={redoRegions}
              disabled={!canRedo}
              title="Redo (Ctrl/⌘+Shift+Z)"
              aria-label="Redo"
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <path d="M21 7v6h-6" />
                <path d="M21 13l-4-4a8 8 0 0 0-13 6" />
              </svg>
            </button>
            <button
              type="button"
              className={`mp-region-btn${drawMode ? ' mp-region-btn-active' : ''}`}
              onClick={() => { if (drawMode) cancelDrawing(); else startDrawingRegion(); }}
              title={drawMode ? 'Cancel drawing' : 'Draw a region (click points, double-click to close)'}
              aria-pressed={drawMode}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <polygon points="12 2 22 8.5 19 19 5 19 2 8.5" />
              </svg>
              <span className="mp-region-btn-text">{drawMode ? 'Cancel' : 'Draw Region'}</span>
            </button>
            <button
              type="button"
              className={`mp-region-btn${deleteMode ? ' mp-region-btn-active' : ''}`}
              onClick={() => { cancelDrawing(); setAddingLabel(false); setDeleteMode(v => !v); }}
              title="Click a region or label on the map to delete it"
              aria-pressed={deleteMode}
              disabled={regions.length === 0 && labels.length === 0}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
              </svg>
              <span className="mp-region-btn-text">{deleteMode ? 'Done' : 'Delete'}</span>
            </button>
            <button
              type="button"
              className={`mp-region-btn${addingLabel ? ' mp-region-btn-active' : ''}`}
              onClick={() => {
                if (addingLabel) { setAddingLabel(false); return; }
                cancelDrawing();
                setDeleteMode(false);
                setGeocodeOpen(false);
                setAddingLabel(true);
              }}
              title={addingLabel ? 'Cancel adding label' : 'Add a text label (click anywhere on the map to place it)'}
              aria-pressed={addingLabel}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <path d="M5 5h14M12 5v14" />
              </svg>
              <span className="mp-region-btn-text">{addingLabel ? 'Cancel' : 'Add Label'}</span>
            </button>
            <button
              type="button"
              className={`mp-region-btn${geocodeOpen ? ' mp-region-btn-active' : ''}`}
              onClick={() => {
                if (geocodeOpen) {
                  setGeocodeOpen(false);
                  setGeocodeQuery('');
                  setGeocodeResults(null);
                  setGeocodeError('');
                  return;
                }
                cancelDrawing();
                setDeleteMode(false);
                setAddingLabel(false);
                setGeocodeOpen(true);
              }}
              title={geocodeOpen ? 'Cancel search' : 'Add a region by searching for a country, region, or place name'}
              aria-pressed={geocodeOpen}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                strokeLinecap="round" strokeLinejoin="round" width="13" height="13">
                <circle cx="11" cy="11" r="7" />
                <line x1="16.5" y1="16.5" x2="22" y2="22" />
              </svg>
              <span className="mp-region-btn-text">{geocodeOpen ? 'Cancel' : 'Add Region from Name'}</span>
            </button>
          </div>
        )}

        {/* Geocode search panel — editor only, when the tool is armed */}
        {canEdit && geocodeOpen && (
          <div className="mp-geocode-panel" role="dialog" aria-label="Find region by name">
            <div className="mp-geocode-row">
              <input
                type="text"
                className="mp-geocode-input"
                value={geocodeQuery}
                onChange={e => setGeocodeQuery(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); submitGeocode(); } }}
                placeholder="e.g. France, Anatolia, Nile Delta"
                maxLength={120}
                autoFocus
                disabled={geocodeBusy}
              />
              <button
                type="button"
                className="mp-geocode-search-btn"
                onClick={submitGeocode}
                disabled={geocodeBusy || !geocodeQuery.trim()}
                title="Search"
              >
                {geocodeBusy ? '…' : 'Search'}
              </button>
            </div>
            <p className="mp-geocode-hint">
              Works best with modern countries and regions. For historical territories use the draw tool.
            </p>
            {geocodeError && (
              <p className="mp-geocode-error" role="alert">{geocodeError}</p>
            )}
            {Array.isArray(geocodeResults) && geocodeResults.length > 0 && (
              <ul className="mp-geocode-results" role="listbox" aria-label="Search results">
                {geocodeResults.map((r, i) => {
                  const hasBoundary = !!r.geojson && (r.geojson.type === 'Polygon' || r.geojson.type === 'MultiPolygon');
                  return (
                    <li key={`${r.lat}-${r.lon}-${i}`}>
                      <button
                        type="button"
                        className="mp-geocode-result"
                        onClick={() => applyGeocodeResult(r)}
                        disabled={!hasBoundary}
                        title={hasBoundary ? 'Draw this boundary' : 'No boundary data (point only)'}
                      >
                        <span className="mp-geocode-result-name">{r.displayName}</span>
                        <span className="mp-geocode-result-type">
                          {r.type || r.placeClass || 'place'}{hasBoundary ? '' : ' · point only'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}

        {/* Region popover — editor only, when a polygon was just drawn or an existing region is being edited */}
        {canEdit && popoverForm && (
          <div className="mp-region-popover" role="dialog" aria-label="Region details">
            <div className="mp-region-popover-title">
              {editingRegion ? 'Edit region' : 'New region'}
            </div>
            <div className="mp-region-popover-body">
            <label className="mp-region-popover-row">
              <span>Label</span>
              <input
                type="text"
                value={popoverForm.label}
                onChange={e => {
                  const next = { ...popoverForm, label: e.target.value };
                  setPopoverForm(next);
                  liveApply(next, { debounce: true });
                }}
                placeholder="e.g. Roman Empire"
                maxLength={60}
                autoFocus
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Color</span>
              <input
                type="color"
                value={popoverForm.color}
                onChange={e => {
                  const next = { ...popoverForm, color: e.target.value };
                  setPopoverForm(next);
                  liveApply(next);
                }}
                aria-label="Region color"
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Opacity</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <input
                  type="range"
                  min={0.1}
                  max={0.6}
                  step={0.05}
                  value={popoverForm.opacity}
                  onChange={e => {
                    const next = { ...popoverForm, opacity: parseFloat(e.target.value) };
                    setPopoverForm(next);
                    liveApply(next, { debounce: true });
                  }}
                  aria-label="Region opacity"
                  style={{ flex: 1 }}
                />
                <span className="mp-region-popover-pct">{Math.round(popoverForm.opacity * 100)}%</span>
              </div>
            </label>
            <label className="mp-region-popover-row">
              <span>Border</span>
              <div className="mp-region-segmented" role="group" aria-label="Border style">
                {[
                  { id: 'solid',  label: '—' },
                  { id: 'dashed', label: '- -' },
                  { id: 'dotted', label: '· · ·' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`mp-region-seg${popoverForm.borderStyle === opt.id ? ' mp-region-seg-active' : ''}`}
                    onClick={() => {
                      const next = { ...popoverForm, borderStyle: opt.id };
                      setPopoverForm(next);
                      liveApply(next);
                    }}
                    aria-pressed={popoverForm.borderStyle === opt.id}
                    title={opt.id.charAt(0).toUpperCase() + opt.id.slice(1)}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </label>
            <label className="mp-region-popover-row">
              <span>Thickness</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                <input
                  type="range"
                  min={1}
                  max={6}
                  step={1}
                  value={popoverForm.borderThickness}
                  onChange={e => {
                    const next = { ...popoverForm, borderThickness: parseInt(e.target.value, 10) };
                    setPopoverForm(next);
                    liveApply(next, { debounce: true });
                  }}
                  aria-label="Border thickness"
                  style={{ flex: 1 }}
                />
                <span className="mp-region-popover-pct">{popoverForm.borderThickness}px</span>
              </div>
            </label>
            <label className="mp-region-popover-row">
              <span>Visible from</span>
              <input
                type="text"
                value={popoverForm.visibleFrom || ''}
                onChange={e => {
                  const next = { ...popoverForm, visibleFrom: e.target.value };
                  setPopoverForm(next);
                  liveApply(next, { debounce: true });
                }}
                placeholder="(always)"
                aria-label="Visible from date"
                style={{ flex: 1 }}
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Visible until</span>
              <input
                type="text"
                value={popoverForm.visibleUntil || ''}
                onChange={e => {
                  const next = { ...popoverForm, visibleUntil: e.target.value };
                  setPopoverForm(next);
                  liveApply(next, { debounce: true });
                }}
                placeholder="(always)"
                aria-label="Visible until date"
                style={{ flex: 1 }}
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Fill</span>
              <div className="mp-region-fillgrid" role="group" aria-label="Fill style">
                {[
                  { id: 'solid',      label: 'Solid'       },
                  { id: 'hatching',   label: 'Diagonal'    },
                  { id: 'crosshatch', label: 'Crosshatch'  },
                  { id: 'dots',       label: 'Dots'        },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`mp-region-swatch mp-region-swatch-${opt.id}${popoverForm.fillStyle === opt.id ? ' mp-region-swatch-active' : ''}`}
                    onClick={() => {
                      const next = { ...popoverForm, fillStyle: opt.id };
                      setPopoverForm(next);
                      liveApply(next);
                    }}
                    aria-pressed={popoverForm.fillStyle === opt.id}
                    title={opt.label}
                    style={{ '--swatch-color': popoverForm.color }}
                  />
                ))}
              </div>
            </label>
            <label className="mp-region-popover-row">
              <span>Label size</span>
              <div className="mp-region-segmented" role="group" aria-label="Label size">
                {[
                  { id: 'small',  label: 'S' },
                  { id: 'medium', label: 'M' },
                  { id: 'large',  label: 'L' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`mp-region-seg${(popoverForm.labelSize || 'medium') === opt.id ? ' mp-region-seg-active' : ''}`}
                    onClick={() => {
                      const next = { ...popoverForm, labelSize: opt.id };
                      setPopoverForm(next);
                      liveApply(next);
                    }}
                    aria-pressed={popoverForm.labelSize === opt.id}
                    title={opt.id}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </label>
            <label className="mp-region-popover-row mp-region-popover-row-multi">
              <span>Linked events</span>
              <div className="mp-region-linked-multi" role="group" aria-label="Linked events">
                {sorted.length === 0 && (
                  <p style={{ fontSize: 11, color: 'var(--text3)', fontStyle: 'italic', margin: 0 }}>
                    No events on this timeline yet.
                  </p>
                )}
                {sorted.map(ev => {
                  const ids = Array.isArray(popoverForm.linkedEventIds) ? popoverForm.linkedEventIds : [];
                  const checked = ids.includes(ev.id);
                  return (
                    <label key={ev.id} className="mp-region-linked-row">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          const nextIds = e.target.checked
                            ? [...ids.filter(id => id !== ev.id), ev.id]
                            : ids.filter(id => id !== ev.id);
                          const nextForm = { ...popoverForm, linkedEventIds: nextIds };
                          setPopoverForm(nextForm);
                          liveApply(nextForm);
                        }}
                      />
                      <span>{ev.title || '(untitled)'}</span>
                    </label>
                  );
                })}
              </div>
            </label>
            </div>
            <div className="mp-region-popover-actions">
              <button type="button" className="mp-region-popover-cancel" onClick={cancelRegionForm}>Cancel</button>
              <button type="button" className="mp-region-popover-save" onClick={saveRegionForm} style={{ background: popoverForm.color, color: '#1A1408' }}>Save</button>
            </div>
          </div>
        )}

        {/* Standalone label popover — editor only, when placing or editing a label */}
        {canEdit && labelForm && editingLabel && (
          <div className="mp-region-popover" role="dialog" aria-label="Label details">
            <div className="mp-region-popover-title">
              {editingLabelOriginalRef.current ? 'Edit label' : 'New label'}
            </div>
            <div className="mp-region-popover-body">
            <label className="mp-region-popover-row">
              <span>Text</span>
              <input
                type="text"
                value={labelForm.text}
                onChange={e => {
                  const next = { ...labelForm, text: e.target.value };
                  setLabelForm(next);
                  liveApplyLabel(next, { debounce: true });
                }}
                placeholder="e.g. Atlantic Ocean"
                maxLength={80}
                autoFocus
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Font size</span>
              <div className="mp-region-segmented" role="group" aria-label="Font size">
                {[
                  { id: 'small',  label: 'S' },
                  { id: 'medium', label: 'M' },
                  { id: 'large',  label: 'L' },
                ].map(opt => (
                  <button
                    key={opt.id}
                    type="button"
                    className={`mp-region-seg${labelForm.fontSize === opt.id ? ' mp-region-seg-active' : ''}`}
                    onClick={() => {
                      const next = { ...labelForm, fontSize: opt.id };
                      setLabelForm(next);
                      liveApplyLabel(next);
                    }}
                    aria-pressed={labelForm.fontSize === opt.id}
                    title={opt.id}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </label>
            <label className="mp-region-popover-row">
              <span>Text color</span>
              <input
                type="color"
                value={labelForm.textColor}
                onChange={e => {
                  const next = { ...labelForm, textColor: e.target.value };
                  setLabelForm(next);
                  liveApplyLabel(next);
                }}
                aria-label="Label text color"
              />
            </label>
            <label className="mp-region-popover-row">
              <span>Background</span>
              <input
                type="checkbox"
                checked={!!labelForm.showBackground}
                onChange={e => {
                  const next = { ...labelForm, showBackground: e.target.checked };
                  setLabelForm(next);
                  liveApplyLabel(next);
                }}
                aria-label="Show background pill"
                style={{ marginRight: 'auto' }}
              />
            </label>
            {labelForm.showBackground && (
              <>
                <label className="mp-region-popover-row">
                  <span>Bg color</span>
                  <input
                    type="color"
                    value={labelForm.backgroundColor}
                    onChange={e => {
                      const next = { ...labelForm, backgroundColor: e.target.value };
                      setLabelForm(next);
                      liveApplyLabel(next);
                    }}
                    aria-label="Background color"
                  />
                </label>
                <label className="mp-region-popover-row">
                  <span>Bg opacity</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={labelForm.backgroundOpacity}
                      onChange={e => {
                        const next = { ...labelForm, backgroundOpacity: parseFloat(e.target.value) };
                        setLabelForm(next);
                        liveApplyLabel(next, { debounce: true });
                      }}
                      aria-label="Background opacity"
                      style={{ flex: 1 }}
                    />
                    <span className="mp-region-popover-pct">{Math.round(labelForm.backgroundOpacity * 100)}%</span>
                  </div>
                </label>
              </>
            )}
            </div>
            <div className="mp-region-popover-actions">
              <button type="button" className="mp-region-popover-cancel" onClick={cancelLabelForm}>Cancel</button>
              {editingLabelOriginalRef.current && (
                <button
                  type="button"
                  className="mp-region-popover-cancel"
                  onClick={deleteLabelFromForm}
                  style={{ color: 'var(--danger, #c0392b)' }}
                  title="Delete this label"
                >
                  Delete
                </button>
              )}
              <button type="button" className="mp-region-popover-save" onClick={saveLabelForm}>Save</button>
            </div>
          </div>
        )}

        {/* Tile toggle moved to Design tab → Map Style picker. The floating
            control was redundant and added noise to the map surface. */}

        {/* ENH 2: linked-event popup card (region click → event preview) */}
        {linkedEventPopup && (
          <LinkedEventPopup
            events={linkedEventPopup.events || (linkedEventPopup.event ? [linkedEventPopup.event] : [])}
            onClose={() => setLinkedEventPopup(null)}
            onView={(ev) => { onEventClick?.(ev); setLinkedEventPopup(null); }}
            primary={primary}
          />
        )}

        {eventsWithLoc.length === 0 && (
          <div className="mp-no-pins-overlay">
            No events have location data yet. Edit events to add locations.
          </div>
        )}
      </div>

      {/* ── Date scrubber (feature 4) ──────────────────────────────────────── */}
      {sorted.length > 0 && scrubMin && scrubMax && scrubMin !== scrubMax && (
        <div className="mp-date-scrub" role="group" aria-label="Date scrubber">
          <button
            type="button"
            className="mp-date-scrub-play"
            onClick={toggleScrubPlay}
            aria-label={scrubPlaying ? 'Pause' : 'Play through dates'}
            title={scrubPlaying ? 'Pause' : 'Play through the timeline'}
          >
            {scrubPlaying
              ? <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg>
              : <svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3"/></svg>}
          </button>
          <div className="mp-date-scrub-track">
            <div className="mp-date-scrub-label" style={{ left: `${scrubFrac * 100}%` }}>{scrubLabel}</div>
            <input
              type="range"
              min={0}
              max={1}
              step={0.001}
              value={scrubFrac}
              onChange={e => { setScrubPlaying(false); setScrubFrac(parseFloat(e.target.value)); }}
              aria-label="Scrub through timeline dates"
            />
          </div>
          <div className="mp-date-scrub-speeds" role="group" aria-label="Playback speed">
            {SCRUB_SPEEDS.map((s, i) => (
              <button
                key={s.label}
                type="button"
                className={`mp-date-scrub-speed${scrubSpeed === i ? ' mp-date-scrub-speed-active' : ''}`}
                onClick={() => setScrubSpeed(i)}
              >
                {s.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── Scrubber zone ───────────────────────────────────────────────────── */}
      <div className="mp-scrubber-zone">
        {/* Event filter */}
        <div className="mp-filter-row">
          <div className="mp-filter-wrap">
            <svg className="mp-filter-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
              strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
              <circle cx="11" cy="11" r="7" /><line x1="16.5" y1="16.5" x2="22" y2="22" />
            </svg>
            <input
              className="mp-filter-input"
              type="text"
              placeholder="Filter events…"
              value={filterQuery}
              onChange={e => setFilterQuery(e.target.value)}
              aria-label="Filter events by title or description"
            />
            {filterQuery && (
              <button className="mp-filter-clear" onClick={() => setFilterQuery('')} aria-label="Clear filter">✕</button>
            )}
          </div>
          {filterQuery && <span className="mp-filter-count">{filteredEvents.length} / {sorted.length}</span>}
        </div>

        {/* Card track */}
        <div className="mp-scrubber-row">
          <button className="mp-nav-btn" onClick={() => navigate(-1)} disabled={activeIdx === 0} aria-label="Previous event">‹</button>

          <div ref={scrubberRef} className="mp-scrubber-track" role="listbox" aria-label="Timeline events">
            {filteredEvents.map((ev) => {
              const hasLoc   = hasValidCoords(ev);
              const realIdx  = sorted.indexOf(ev);
              const isActive = realIdx === activeIdx;
              const catCol   = categoryColor(ev, primary);
              return (
                <button
                  key={ev.id}
                  data-ev-id={ev.id}
                  className={`mp-card${isActive ? ' mp-card-active' : ''}${!hasLoc ? ' mp-card-noloc' : ''}`}
                  role="option"
                  aria-selected={isActive}
                  onClick={() => { setActiveIdx(realIdx); if (!hasLoc) showToast('This event has no location set'); }}
                  title={ev.title}
                  style={isActive ? { borderColor: primary } : {}}
                >
                  {ev.category && <div className="mp-card-cat-dot" style={{ background: catCol }} title={ev.category} />}
                  <div className="mp-card-title">{ev.title}</div>
                  <div className="mp-card-date">{formatDate(ev.startDate)}</div>
                  {!hasLoc && <div className="mp-card-noloc-badge">No location</div>}
                </button>
              );
            })}
            {filteredEvents.length === 0 && (
              <div className="mp-filter-empty">No events match "{filterQuery}"</div>
            )}
          </div>

          <button className="mp-nav-btn" onClick={() => navigate(1)} disabled={activeIdx === sorted.length - 1} aria-label="Next event">›</button>
        </div>

        {/* Play controls */}
        <div className="mp-play-row">
          <button
            className={`mp-play-btn${playing ? ' mp-playing' : ''}`}
            onClick={() => setPlaying(p => !p)}
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? (
              <><svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><rect x="6" y="4" width="4" height="16" rx="1"/><rect x="14" y="4" width="4" height="16" rx="1"/></svg> Pause</>
            ) : (
              <><svg viewBox="0 0 24 24" fill="currentColor" width="11" height="11"><polygon points="5 3 19 12 5 21 5 3"/></svg> Play</>
            )}
          </button>

          <div className="mp-speed-group" aria-label="Playback speed">
            {SPEEDS.map((s, i) => (
              <button
                key={s.label}
                className={`mp-speed-btn${playSpeed === i ? ' mp-speed-active' : ''}`}
                onClick={() => setPlaySpeed(i)}
                title={`${s.label} — ${s.ms / 1000}s per event`}
              >
                {s.label}
              </button>
            ))}
          </div>

          <div className="mp-progress" role="progressbar" aria-valuenow={Math.round(progress)} aria-valuemin="0" aria-valuemax="100" style={{ opacity: playing ? 1 : 0.25 }}>
            <div className="mp-progress-fill" style={{ width: `${progress}%`, background: primary }} />
          </div>

          <span className="mp-counter" aria-live="polite">{activeIdx + 1} / {sorted.length}</span>
        </div>
      </div>
    </div>

    {/* ── Embed modal ── */}
    {embedOpen && timeline && (
      <Modal open onClose={() => { setEmbedOpen(false); setEmbedCopied(false); }} title="Embed Timeline" width={540}>
        <p style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 16, lineHeight: 1.6 }}>
          Paste this snippet into any webpage to embed <strong style={{ color: 'var(--text)' }}>{timeline.title}</strong> as an interactive iframe.
        </p>
        <div style={{ background: 'var(--bg3)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', overflow: 'hidden', marginBottom: 12 }}>
          <pre style={{ padding: '14px 16px', margin: 0, fontSize: 12, lineHeight: 1.75, color: 'var(--accent2)', fontFamily: 'monospace', overflowX: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
            {embedCode}
          </pre>
        </div>
        <p style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 20 }}>
          Embed URL: <a href={embedUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent)' }}>{embedUrl}</a>
        </p>
        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <button className="btn-ghost" onClick={() => { setEmbedOpen(false); setEmbedCopied(false); }}>Close</button>
          <button className="btn-primary" onClick={() => { navigator.clipboard?.writeText(embedCode).catch(() => {}); setEmbedCopied(true); setTimeout(() => setEmbedCopied(false), 2000); }} style={{ minWidth: 148 }}>
            {embedCopied ? 'Copied!' : 'Copy Snippet'}
          </button>
        </div>
      </Modal>
    )}
    </>
  );
};

export default MapTimeline;
